import { useState, useCallback, useRef, useEffect } from "react";
import type { TranslateFn } from "../../i18n";
import { ApiClient, type StyleMatchRequestProduct, type StyleMatchSelection } from "../../api-client";
import { compressImage, checkAgeBeforeUpload } from "../../image-utils";
import type { ChestProfile, MidsectionProfile, HipProfile, PrimeStyleOutfitItem, PrimeStyleOutfitLook } from "../types";
import { isImperial, getUnitLabel } from "../utils/units";
import { useIsMobile } from "../utils/useIsMobile";
import {
  ArrowLeftIcon,
  UploadIcon,
  ChevronDownIcon,
  SparkleIcon,
} from "../icons";
import { BasicsStepMobile } from "./BasicsStepMobile";
import { PhotoStepMobile } from "./PhotoStepMobile";
import { BodyShapeStepMobile } from "./BodyShapeStepMobile";
import { MobileBottomTabs } from "../components/MobileBottomTabs";
import { normalizeProfilePhotoSource, profilePhotoToBlob, profilePhotoToDataUrl } from "../utils/profile-image";
// No unit conversion imports — backend handles all math

/* ── Body shape illustrations ── */
import { BODY_ILLUSTRATIONS } from "../assets/body-illustrations";
import { BODY_IMAGES } from "../assets/body-illustrations-png";
import shoeIllustration from "../assets/outfit-builder/shoe-illustration.png";
import bagIllustration from "../assets/outfit-builder/bag-illustration.png";
import accessoriesIllustration from "../assets/outfit-builder/accessories-illustration.png";
import topIllustration from "../assets/outfit-builder/top-illustration.png";
import bottomIllustration from "../assets/outfit-builder/bottom-illustration.png";
import dressIllustration from "../assets/outfit-builder/dress-illustration.png";
import outerwearIllustration from "../assets/outfit-builder/outerwear-illustration.png";
import styleIllustration from "../assets/outfit-builder/style-illustration.png";
import {
  SHOE_BRANDS,
  SHOE_SIZE_SYSTEM_OPTIONS,
  estimateShoeReference,
  formatShoeFootLength,
  getDefaultShoeSizeSystem,
  getShoeSizeOptions,
  inferShoeGender,
  type ShoeGender,
  type ShoeSizeSystem,
} from "../utils/shoe-reference";

function BodyIllustration({ svg }: { svg: string }) {
  return <div className="ps-bp-illust" dangerouslySetInnerHTML={{ __html: svg }} />;
}

/* ── Icons ── */

function CameraUploadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="3 8 7 12 13 4" />
    </svg>
  );
}

/* ── Step progress dots ── */
function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="ps-bp-dots">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`ps-bp-dot${i < current ? " ps-bp-dot-done" : ""}${i === current ? " ps-bp-dot-active" : ""}`}
        />
      ))}
    </div>
  );
}

/* ── Option card ── */
function OptionCard<T extends string>({
  value,
  label,
  icon,
  selected,
  onSelect,
}: {
  value: T;
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onSelect: (v: T) => void;
}) {
  return (
    <button
      className={`ps-bp-option${selected ? " ps-bp-option-selected" : ""}`}
      onClick={() => onSelect(value)}
      type="button"
    >
      <div className="ps-bp-option-icon">{icon}</div>
      <span className="ps-bp-option-label">{label}</span>
    </button>
  );
}

/* ── Image option card for body shape grid ── */
function ImageOptionCard({
  img,
  svgFallback,
  label,
  selected,
  onSelect,
  onHover,
  onLeave,
}: {
  img: string | null;
  svgFallback?: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  return (
    <button
      className={`ps-bp-img-card${selected ? " ps-bp-img-card-selected" : ""}`}
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      type="button"
    >
      <span className="ps-bp-img-card-check">
        <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2.5 6 5 8.5 9.5 3.5" /></svg>
      </span>
      <div className="ps-bp-img-card-thumb">
        {img ? (
          <img src={img} alt={label} className="ps-bp-img-card-img" />
        ) : svgFallback ? (
          <div className="ps-bp-illust" dangerouslySetInnerHTML={{ __html: svgFallback }} />
        ) : null}
      </div>
      <span className="ps-bp-img-card-label">{label}</span>
    </button>
  );
}

/* ── Main BodyProfileView ── */

interface BodyProfileViewProps {
  productImage: string;
  productImages?: string[];
  productCarouselItems?: Array<{ image: string; title?: string; href?: string }>;
  styleMatchProduct?: StyleMatchRequestProduct;
  outfitBuilderSource?: "sdk" | "ai-stylist";
  instantOutfitLooks?: PrimeStyleOutfitLook[];
  currentRecommendedSize?: string | null;
  productTitle: string;
  isWomen: boolean;
  basicOnly?: boolean;
  /** Optional entry step for recovery flows, such as falling back from an error to manual measurements. */
  initialStep?: "photo" | "basics";
  /** Use the default upload screen with only height/weight questions. */
  simplePhotoOnly?: boolean;
  /** Compact desktop treatment for accessory flows that only need height/weight. */
  simpleAccessoryLayout?: boolean;
  simpleQuestionMode?: "heightWeight" | "shoeReference";
  formRef: React.MutableRefObject<Record<string, string>>;
  sizingCountry: string;
  heightUnit: string;
  setHeightUnit: (u: string) => void;
  weightUnit: string;
  setWeightUnit: (u: string) => void;
  sizingUnit?: string;
  setSizingUnit?: (u: string) => void;
  /** Backend URL + API key — required for the pre-upload age check call. */
  apiUrl?: string;
  apiKey?: string;
  onComplete: (data: {
    height: number;
    weight: number;
    heightUnit: string;
    weightUnit: string;
    gender: string;
    age?: number;
    chestProfile?: ChestProfile;
    midsectionProfile?: MidsectionProfile;
    hipProfile?: HipProfile;
    bodyImage?: string;
    bandSize?: string;
    cupSize?: string;
    braSizeRegion?: string;
  }) => void;
  onSnapSubmit: (data: {
    photoFile: File;
    photoBase64: string;
    height: number;
    weight: number;
    heightUnit: string;
    weightUnit: string;
    sizingUnit?: string;
    gender: string;
    age?: number;
    bandSize?: string;
    cupSize?: string;
    braSizeRegion?: string;
    braSize?: { band: number; cup: string; region: string };
    chestProfile?: ChestProfile;
    midsectionProfile?: MidsectionProfile;
    hipProfile?: HipProfile;
    extraMeasurements?: Record<string, string | number>;
    styleMatchSelections?: StyleMatchSelection[];
  }) => void;
  /** True if the user has an active profile with stored measurements —
   *  changes the basics step Next button to "Find My Best Fit" */
  hasActiveProfileWithMeasurements?: boolean;
  /** Fast-path callback: skip the rest of the wizard and run /sizing/recommend
   *  with the active profile's stored measurements directly. */
  onUseActiveProfile?: (styleMatchSelections?: StyleMatchSelection[], requestedSizingUnit?: "cm" | "in") => void;
  /** Display name of the currently active profile (for the "Profile X loaded"
   *  banner shown on the basics step). */
  activeProfileName?: string | null;
  /** Deselect the active profile so the user can run a fresh AI sizing flow
   *  with full body-shape questions. */
  onStartFresh?: () => void;
  /** Active profile — when set, its height/weight/age/gender seed the basics
   *  inputs so the SDK opens with the user's saved values, not the defaults. */
  activeProfile?: import("../types").Profile | null;
  onBack: () => void;
  t: TranslateFn;
}

// Men: basics → chest → midsection → seat
// Women: basics → midsection → seat → hips → bra
// AI Scan flow on mobile: scan-details → photo (2-step wizard)
type Step = "basics" | "scan-details" | "style-preview" | "photo" | "body-shapes" | "all-in-one" | "chest" | "bra" | "hips" | "midsection" | "seat";
const MALE_STEPS: Step[] = ["basics", "chest", "midsection", "seat"];
const FEMALE_STEPS: Step[] = ["basics", "midsection", "seat", "hips", "bra"];
// "photo" and "scan-details" are sub-steps of basics — don't appear in progress dots

// Bra size data by region
const BAND_SIZES: Record<string, string[]> = {
  US: ["28","30","32","34","36","38","40","42","44","46","48","50","52","54","56"],
  UK: ["28","30","32","34","36","38","40","42","44","46","48","50","52","54","56"],
  EU: ["60","65","70","75","80","85","90","95","100","105","110","115","120"],
  FR: ["80","85","90","95","100","105","110","115","120"],
  IT: ["0","1","2","3","4","5","6","7","8","9","10"],
  JP: ["60","65","70","75","80","85","90","95","100"],
};
const CUP_SIZES: Record<string, string[]> = {
  US: ["AA","A","B","C","D","DD/E","DDD/F","G","H","I","J"],
  UK: ["AA","A","B","C","D","DD","E","F","FF","G","GG","H"],
  EU: ["AA","A","B","C","D","E","F","G","H","I","J"],
  FR: ["AA","A","B","C","D","E","F","G","H","I","J"],
  IT: ["AA","A","B","C","D","DD","E","F","G","H","I"],
  JP: ["AA","A","B","C","D","E","F","G","H","I"],
};

// Cup offset: each cup adds ~1 inch (2.54 cm) to bust over underbust
const CUP_OFFSETS: Record<string, number> = {
  "AA": 0, "A": 1, "B": 2, "C": 3, "D": 4,
  "DD": 5, "DD/E": 5, "E": 5, "DDD/F": 6, "F": 6, "FF": 7,
  "G": 7, "GG": 8, "H": 8, "I": 9, "J": 10,
};

/**
 * Compact animated select used inside the photo step's body details column
 * (region / band / cup pickers for women). Uses a button trigger + popover
 * panel with slide-fade animation. Click-outside closes via a single shared
 * "open menu" key managed by the parent so opening one closes others.
 */
function MiniSelect({
  value, options, placeholder, isOpen, onToggle, onSelect, direction = "up",
}: {
  value: string | null;
  options: string[];
  placeholder?: string;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (v: string) => void;
  /** Where the panel opens relative to the trigger. Bra pickers default up
   *  so mobile panels do not collide with the bottom CTA. */
  direction?: "up" | "down";
}) {
  return (
    <div className="ps-bp-mini-select" data-open={isOpen ? "true" : "false"} data-dir={direction}>
      <button
        type="button"
        className="ps-bp-mini-select-trigger"
        onClick={onToggle}
      >
        <span className="ps-bp-mini-select-value">
          {value || <span className="ps-bp-mini-select-placeholder">{placeholder ?? "—"}</span>}
        </span>
        <span className={`ps-bp-mini-select-arrow${isOpen ? " ps-open" : ""}`}>&#9662;</span>
      </button>
      {isOpen && (
        <div className="ps-bp-mini-select-panel" role="listbox">
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={value === opt}
              className={`ps-bp-mini-select-item${value === opt ? " ps-selected" : ""}`}
              onClick={() => onSelect(opt)}
            >{opt}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function getBustMeasurement(band: string, cup: string, region: string): { cm: number; inch: number } | null {
  const cupOffset = CUP_OFFSETS[cup];
  if (cupOffset == null) return null;
  const bandNum = parseFloat(band);
  if (!bandNum) return null;

  // US/UK/IT band sizes are in inches, EU/FR/JP are in cm
  if (["EU", "FR", "JP"].includes(region)) {
    const bustCm = bandNum + cupOffset * 2.54;
    return { cm: Math.round(bustCm), inch: Math.round(bustCm / 2.54) };
  }
  if (region === "IT") {
    // IT band is an index, convert: IT 1 ≈ 65cm underbust
    const underbustCm = 60 + bandNum * 5;
    const bustCm = underbustCm + cupOffset * 2.54;
    return { cm: Math.round(bustCm), inch: Math.round(bustCm / 2.54) };
  }
  // US/UK — band is inches
  const bustInch = bandNum + cupOffset;
  return { cm: Math.round(bustInch * 2.54), inch: Math.round(bustInch) };
}

type StylePreviewOption = {
  slot?: StyleMatchSelection["slot"];
  productId: string;
  image: string;
  displayImage?: string;
  title: string;
  url?: string;
  color: string;
  garmentType: string;
  recommendedSize: string;
};

type StylePreviewSourceItem = Pick<PrimeStyleOutfitItem, "slot" | "productId" | "image" | "title"> &
  Partial<Pick<PrimeStyleOutfitItem, "displayImage" | "url" | "color" | "garmentType" | "recommendedSize" | "selected">> & {
    alternatives?: Array<Omit<PrimeStyleOutfitItem, "alternatives" | "selected">>;
  };

type StylePreviewRow = StylePreviewOption & {
  id: string;
  selected: boolean;
  options?: StylePreviewOption[];
  optionIndex?: number;
};

type StylePreviewOutfit = {
  id: string;
  label?: string;
  rows: StylePreviewRow[];
};

type GarmentCutoutCacheEntry = {
  promise: Promise<string>;
  resolved?: string;
};

const garmentCutoutCache = new Map<string, GarmentCutoutCacheEntry>();

function garmentCutoutCacheKey(imageUrl: string, apiUrl?: string): string {
  return `${apiUrl || "http://localhost:4000"}\u0001${imageUrl}`;
}

function cleanupGarmentBackground(
  imageUrl: string,
  apiKey?: string,
  apiUrl?: string,
): Promise<string> {
  if (!apiKey || imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")) {
    return Promise.resolve(imageUrl);
  }
  const key = garmentCutoutCacheKey(imageUrl, apiUrl);
  const cached = garmentCutoutCache.get(key);
  if (cached) return cached.promise;

  const entry = {} as GarmentCutoutCacheEntry;
  entry.promise = new ApiClient(apiKey, apiUrl)
    .removeGarmentBackground(imageUrl)
    .then((cutout) => {
      entry.resolved = cutout;
      return cutout;
    })
    .catch(() => imageUrl);
  garmentCutoutCache.set(key, entry);
  return entry.promise;
}

function resolvedGarmentBackground(imageUrl: string, apiUrl?: string): string | undefined {
  return garmentCutoutCache.get(garmentCutoutCacheKey(imageUrl, apiUrl))?.resolved;
}

function StylePreviewGarmentImage({
  src,
  alt,
  apiKey,
  apiUrl,
  removeBackground = false,
  eager = false,
}: {
  src: string;
  alt: string;
  apiKey?: string;
  apiUrl?: string;
  removeBackground?: boolean;
  eager?: boolean;
}) {
  const needsCutout = removeBackground && Boolean(apiKey) && !src.startsWith("data:") && !src.startsWith("blob:");
  const [cleanSrc, setCleanSrc] = useState(() => (
    needsCutout ? (resolvedGarmentBackground(src, apiUrl) || src) : src
  ));

  useEffect(() => {
    let cancelled = false;
    if (!needsCutout) {
      setCleanSrc(src);
      return () => {
        cancelled = true;
      };
    }
    setCleanSrc(resolvedGarmentBackground(src, apiUrl) || src);
    cleanupGarmentBackground(src, apiKey, apiUrl).then((next) => {
      if (!cancelled) setCleanSrc(next);
    });
    return () => {
      cancelled = true;
    };
  }, [apiKey, apiUrl, needsCutout, src]);

  return (
    <img
      className="ps-bp-style-img"
      src={cleanSrc}
      alt={alt}
      draggable={false}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
    />
  );
}

const STYLE_COLOR_RULES: Array<[RegExp, string]> = [
  [/\b(multicolor|multi|floral|flower|print|printed|paisley|pattern)\b/i, "Multicolor"],
  [/\b(black|onyx|noir)\b/i, "Black"],
  [/\b(white|ivory|cream|ecru)\b/i, "White"],
  [/\b(grey|gray|charcoal|silver)\b/i, "Grey"],
  [/\b(blue|denim|navy|indigo)\b/i, "Blue"],
  [/\b(tan|beige|camel|khaki|sand)\b/i, "Tan"],
  [/\b(brown|espresso|chocolate)\b/i, "Brown"],
  [/\b(red|burgundy|crimson)\b/i, "Red"],
  [/\b(green|olive|sage)\b/i, "Green"],
  [/\b(pink|rose|blush)\b/i, "Pink"],
];

function inferStyleColor(title: string): string {
  for (const [pattern, label] of STYLE_COLOR_RULES) {
    if (pattern.test(title)) return label;
  }
  return "Neutral";
}

function inferStyleGarmentType(title: string, slot?: string): string {
  const value = `${slot || ""} ${title}`.toLowerCase();
  if (/\bcapri\b/.test(value) && /\bjeans?\b/.test(value)) return "Capri Jeans";
  if (/\bwide[- ]?leg\b/.test(value) && /\b(trouser|pant)\b/.test(value)) return "Wide Pants";
  if (/\b(trouser|pants|jeans|bottom)\b/.test(value)) return /jeans?/.test(value) ? "Jeans" : "Trousers";
  if (/\bshorts?\b/.test(value)) return "Shorts";
  if (/\b(shirt|blouse|top|tee|t-shirt)\b/.test(value)) return /blouse/.test(value) ? "Blouse" : "Shirt";
  if (/\b(jacket|coat|blazer|outerwear)\b/.test(value)) return "Outerwear";
  if (/\b(dress)\b/.test(value)) return "Dress";
  if (/\b(sandal|sneaker|shoe|boot|heel)\b/.test(value)) return /sandal/.test(value) ? "Sandals" : "Shoes";
  if (/\b(bag|tote|purse|clutch)\b/.test(value)) return "Bag";
  if (/\b(watch|chain|necklace|bracelet|ring|sunglasses|glasses)\b/.test(value)) return "Accessory";
  return "Garment";
}

function stylePreviewOptionFromItem(
  item: StylePreviewSourceItem,
  fallbackSize?: string | null,
  fallbackId?: string,
): StylePreviewOption {
  return {
    slot: item.slot,
    productId: item.productId || fallbackId || item.image,
    image: item.image,
    displayImage: item.displayImage,
    title: item.title,
    url: item.url,
    color: item.color || inferStyleColor(item.title),
    garmentType: item.garmentType || inferStyleGarmentType(item.title, item.slot),
    recommendedSize: item.recommendedSize || fallbackSize || "XS",
  };
}

function rowsFromStyleMatchItems(items: StylePreviewSourceItem[], fallbackSize?: string | null): StylePreviewRow[] {
  return items
    .filter((item) => item.image)
    .slice(0, 6)
    .map((item, index) => {
      const primary = stylePreviewOptionFromItem(item, fallbackSize, `${item.slot}-${index}`);
      const alternatives = (item.alternatives || [])
        .filter((alternative) => alternative.image)
        .map((alternative, alternativeIndex) =>
          stylePreviewOptionFromItem(alternative, fallbackSize, `${item.slot}-${index}-alt-${alternativeIndex}`),
        );
      return {
        id: `${item.slot || "item"}-${index}`,
        ...primary,
        selected: item.selected !== false,
        options: [primary, ...alternatives],
        optionIndex: 0,
      };
    });
}

function outfitsFromStyleMatchResponse(
  outfits: Array<{ id?: string; label?: string; items?: StylePreviewSourceItem[] }> | undefined,
  fallbackSize?: string | null,
): StylePreviewOutfit[] {
  return (outfits || [])
    .map((outfit, index) => ({
      id: outfit.id || `outfit-${index + 1}`,
      label: outfit.label,
      rows: rowsFromStyleMatchItems(outfit.items || [], fallbackSize),
    }))
    .filter((outfit) => outfit.rows.length);
}

function selectedStyleRowsForTryOn(rows: StylePreviewRow[], apiUrl?: string): StyleMatchSelection[] {
  return rows
    .filter((row) => row.selected && row.image && row.title)
    .map((row) => ({
      slot: row.slot,
      productId: row.productId,
      title: row.title,
      image: row.image,
      displayImage: row.displayImage || resolvedGarmentBackground(row.image, apiUrl),
      url: row.url,
      color: row.color,
      garmentType: row.garmentType,
      recommendedSize: row.recommendedSize,
    }));
}

function prepareStyleMatchSelectionForTryOn(
  selection: StyleMatchSelection,
  apiUrl?: string,
): StyleMatchSelection {
  const cleanImage = selection.displayImage
    || resolvedGarmentBackground(selection.image, apiUrl)
    || selection.image;

  return {
    ...selection,
    image: cleanImage,
    displayImage: cleanImage,
  };
}

function stylePreviewCategoryLabel(row: StylePreviewRow): string {
  switch (row.slot) {
    case "shoe":
      return "Shoes";
    case "bag":
      return "Bag";
    case "accessory":
      return "Accessories";
    case "top":
      return "Tops";
    case "bottom":
      return "Bottoms";
    case "dress":
      return "Dresses";
    case "outerwear":
      return "Outerwear";
    default:
      return row.garmentType || "Style";
  }
}

const STYLE_PREVIEW_CATEGORY_ILLUSTRATIONS: Record<string, string> = {
  shoe: shoeIllustration,
  bag: bagIllustration,
  accessory: accessoriesIllustration,
  top: topIllustration,
  bottom: bottomIllustration,
  dress: dressIllustration,
  outerwear: outerwearIllustration,
};

function StylePreviewCategoryIcon({ row }: { row: StylePreviewRow }) {
  const slot = String(row.slot || "").toLowerCase();
  const illustration = STYLE_PREVIEW_CATEGORY_ILLUSTRATIONS[slot] || styleIllustration;
  return (
    <img
      className="ps-bp-outfit-category-illustration"
      src={illustration}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}

export function BodyProfileView({
  productImage,
  productImages,
  productCarouselItems,
  styleMatchProduct,
  outfitBuilderSource = "sdk",
  instantOutfitLooks,
  currentRecommendedSize,
  productTitle,
  isWomen,
  basicOnly = false,
  initialStep,
  simplePhotoOnly = false,
  simpleAccessoryLayout = false,
  simpleQuestionMode = "heightWeight",
  formRef,
  sizingCountry,
  heightUnit,
  setHeightUnit,
  weightUnit,
  setWeightUnit,
  sizingUnit,
  setSizingUnit,
  apiUrl,
  apiKey,
  onComplete,
  onSnapSubmit,
  hasActiveProfileWithMeasurements = false,
  onUseActiveProfile,
  activeProfileName,
  onStartFresh,
  activeProfile,
  onBack,
  t,
}: BodyProfileViewProps) {
  const isShoeReferenceMode = simpleQuestionMode === "shoeReference";
  // Female body/profile flows always collect bra band/cup because the SDK can
  // create or update a reusable sizing profile from the scan. Do not tie this
  // to the current product chart: bottoms may only need waist/hips for the
  // immediate recommendation, but the saved female profile still needs bust.
  const showGenderSpecificDetails = isWomen && !isShoeReferenceMode;
  const steps = basicOnly || simplePhotoOnly ? (["basics"] as Step[]) : (showGenderSpecificDetails ? FEMALE_STEPS : MALE_STEPS);
  // AI photo sizing is the default entry point — manual measurements is
  // a one-click escape via the "Use Manual Measurements" link rendered
  // inside PhotoStepDesktop / PhotoStepMobile.
  const [step, setStep] = useState<Step>(
    simplePhotoOnly
      ? "photo"
      : basicOnly
        ? "basics"
        : initialStep === "basics"
          ? "basics"
          : "photo",
  );
  const stepIdx = step === "photo" ? 0 : steps.indexOf(step);
  const isMobile = useIsMobile();
  const [stylePreviewAvailability, setStylePreviewAvailability] = useState<"loading" | "enabled" | "disabled">(
    isShoeReferenceMode
      ? "disabled"
      : outfitBuilderSource === "ai-stylist"
      ? "enabled"
      : apiKey && !isShoeReferenceMode ? "loading" : "disabled",
  );
  const shouldOfferStylePreview =
    !isShoeReferenceMode &&
    stylePreviewAvailability === "enabled";
  const [stylePreviewRows, setStylePreviewRows] = useState<StylePreviewRow[]>([]);
  const [stylePreviewOutfits, setStylePreviewOutfits] = useState<StylePreviewOutfit[]>([]);
  const [stylePreviewOutfitIndex, setStylePreviewOutfitIndex] = useState(0);
  const [stylePreviewLoading, setStylePreviewLoading] = useState(false);
  const [stylePreviewSubmitting, setStylePreviewSubmitting] = useState(false);
  const stylePreviewSubmitInFlightRef = useRef(false);
  const [stylePreviewMessage, setStylePreviewMessage] = useState<string | null>(null);
  const [, setStylePreviewHangerRowId] = useState<string | null>(null);
  const stylePreviewRequestInFlightRef = useRef(false);

  useEffect(() => {
    if (isShoeReferenceMode) {
      setStylePreviewAvailability("disabled");
      return;
    }
    if (outfitBuilderSource === "ai-stylist") {
      setStylePreviewAvailability("enabled");
      return;
    }
    if (!apiKey) {
      setStylePreviewAvailability("disabled");
      return;
    }

    let cancelled = false;
    setStylePreviewAvailability("loading");
    const client = new ApiClient(apiKey, apiUrl);
    client.getStyleMatchAvailability()
      .then((response) => {
        if (!cancelled) setStylePreviewAvailability(response.enabled ? "enabled" : "disabled");
      })
      .catch(() => {
        if (!cancelled) setStylePreviewAvailability("disabled");
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, apiUrl, isShoeReferenceMode, outfitBuilderSource]);

  // Basics
  const imperial = isImperial(sizingCountry);

  // Seed initial values: prefer the active profile's saved height/weight/age,
  // then fall back to anything previously typed into the form (formRef), then
  // sensible defaults. The user can still edit these once the SDK opens.
  const profileHeight = activeProfile?.height;
  const profileWeight = activeProfile?.weight;
  const profileAge = activeProfile?.age;
  const profileHUnit =
    (activeProfile?.heightUnit as string | undefined) === "ft"
      ? "in"
      : (activeProfile?.heightUnit as string | undefined);
  const profileWUnit = activeProfile?.weightUnit as string | undefined;

  // Seed empty when there's nothing to seed FROM — never invent a fake
  // value just to show numbers in the boxes. Empty + an existing
  // `placeholder` attribute on each <input> is what surfaces the
  // suggested value to the user without committing it as a real input.
  // Otherwise an untouched form ships e.g. age=30 / weight=154, which
  // makes the backend's photo-vs-inputs consistency check fire on a
  // stranger's body.
  const seedHeightCm = (() => {
    if (profileHeight != null && profileHUnit === "cm") return String(Math.round(profileHeight));
    if (profileHeight != null && (profileHUnit === "in" || profileHUnit === "ft"))
      return String(Math.round(profileHeight * 2.54));
    return formRef.current.height || "";
  })();
  const seedTotalIn = (() => {
    if (profileHeight != null && (profileHUnit === "in" || profileHUnit === "ft"))
      return Math.round(profileHeight);
    if (profileHeight != null && profileHUnit === "cm")
      return Math.round(profileHeight / 2.54);
    return null;
  })();
  const seedHeightFeet = seedTotalIn != null
    ? String(Math.floor(seedTotalIn / 12))
    : formRef.current.heightFeet || "";
  const seedHeightInches = seedTotalIn != null
    ? String(seedTotalIn % 12)
    : formRef.current.heightInches || "";

  const seedWeight = (() => {
    if (profileWeight != null) return String(Math.round(profileWeight));
    return formRef.current.weight || "";
  })();
  const seedAge = profileAge != null
    ? String(profileAge)
    : formRef.current.age || "";

  const [height, setHeight] = useState(seedHeightCm);
  const [heightFeet, setHeightFeet] = useState(seedHeightFeet);
  const [heightInches, setHeightInches] = useState(seedHeightInches);
  const [weight, setWeight] = useState(seedWeight);
  const [age, setAge] = useState(seedAge);
  // hUnit values: "cm" (metric, value is cm) or "in" (imperial, value is total inches)
  // Legacy "ft" is mapped to "in" on init for compat with stored profiles.
  // The active profile's stored unit takes priority, then the host-provided
  // heightUnit/weightUnit, then country defaults.
  const [hUnit, setHUnit] = useState(
    profileHUnit || (heightUnit === "ft" ? "in" : (heightUnit || (imperial ? "in" : "cm"))),
  );
  const [wUnit, setWUnit] = useState(
    profileWUnit || weightUnit || (imperial ? "lbs" : "kg"),
  );
  const isImperialMode = hUnit === "in" || wUnit === "lbs";
  const showSimpleAccessoryMobileTabs = simplePhotoOnly && simpleAccessoryLayout && !isShoeReferenceMode;
  const accessoryMetricDefaultAppliedRef = useRef(false);

  useEffect(() => {
    if (accessoryMetricDefaultAppliedRef.current || !simpleAccessoryLayout || isShoeReferenceMode) return;
    accessoryMetricDefaultAppliedRef.current = true;
    if (hUnit === "in") {
      const totalIn = (parseFloat(heightFeet) || 0) * 12 + (parseFloat(heightInches) || 0);
      if (totalIn > 0) setHeight(String(Math.round(totalIn * 2.54)));
    }
    if (wUnit === "lbs" && weight) {
      setWeight(String(Math.round(parseFloat(weight) / 2.205)));
    }
    setHUnit("cm");
    setWUnit("kg");
    setHeightUnit("cm");
    setWeightUnit("kg");
    setSizingUnit?.("cm");
  }, [heightFeet, heightInches, hUnit, isShoeReferenceMode, setHeightUnit, setSizingUnit, setWeightUnit, simpleAccessoryLayout, wUnit, weight]);

  // Photo upload
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [photoStatus, setPhotoStatus] = useState<string>("");
  const [photoRejection, setPhotoRejection] = useState<string | null>(null);
  // True when the photo currently shown was preloaded from the active
  // profile (not freshly uploaded). Drives the "Loaded from profile" pill
  // + Clear button shown above the HEIGHT input.
  const [photoFromProfile, setPhotoFromProfile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Age-gate: null = not answered, true = 18+, false = under 18 (blocked).
  const [ageConfirmed, setAgeConfirmed] = useState<boolean | null>(null);
  // Photo step — "How to take the best photo" popover visibility. Toggled
  // by the ? icon next to the height/weight fields. Do/Don't/Tip content
  // is no longer surfaced on the page by default; it's opt-in to reduce
  // visual clutter on the photo step, as requested.
  const [photoHelpOpen, setPhotoHelpOpen] = useState(false);
  // Hover state for the photo-step upload zone — once age is confirmed
  // (`ageConfirmed === true`) and no photo is uploaded yet, hovering
  // over the dashed drop zone tints its border blue to communicate
  // "this is now active and ready to receive your photo".
  const [uploadHover, setUploadHover] = useState(false);
  // Compact height input shown on the photo step so Gemini/MediaPipe can scale ratios.
  // Pre-populated from the seed height (always in cm for simplicity).
  const [photoStepHeight, setPhotoStepHeight] = useState(() => {
    if (seedTotalIn != null) return String(Math.round(seedTotalIn * 2.54));
    return seedHeightCm;
  });

  // Profiles
  const [chestProfile, setChestProfile] = useState<ChestProfile | null>(null);
  const [midsectionProfile, setMidsectionProfile] = useState<MidsectionProfile | null>(null);
  const [hipProfile, setHipProfile] = useState<HipProfile | null>(null);
  const [seatProfile, setSeatProfile] = useState<HipProfile | null>(null);

  // Hover description for body shape cards
  const [hoverDesc, setHoverDesc] = useState<string>("");

  // Bra size (women only) — seeded from the active profile so the dropdowns
  // populate with the user's saved band/cup/region instead of empty.
  const seedBand = ((activeProfile as any)?.bandSize as string | undefined) ?? null;
  const seedCup = ((activeProfile as any)?.cupSize as string | undefined) ?? null;
  const seedRegion = ((activeProfile as any)?.braSizeRegion as string | undefined)
    ?? ((activeProfile as any)?.braRegion as string | undefined)
    ?? null;
  const [bandSize, setBandSize] = useState<string | null>(seedBand);
  const [cupSize, setCupSize] = useState<string | null>(seedCup);
  const [braSizeRegion, setBraSizeRegion] = useState<string>(() => {
    if (seedRegion) return seedRegion;
    if (["US", "UK", "AU"].includes(sizingCountry)) return sizingCountry === "AU" ? "UK" : sizingCountry;
    if (["FR", "ES"].includes(sizingCountry)) return "FR";
    if (["IT"].includes(sizingCountry)) return "IT";
    if (["JP", "CN", "KR"].includes(sizingCountry)) return "JP";
    return "EU";
  });
  // Re-seed when the user picks a different profile mid-session — the
  // useState initializers only fire on mount, so without this the dropdowns
  // stay on the first profile's values forever.
  const seededBraProfileIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = (activeProfile as any)?.id ?? null;
    if (!id || seededBraProfileIdRef.current === id) return;
    seededBraProfileIdRef.current = id;
    const b = (activeProfile as any)?.bandSize as string | undefined;
    const c = (activeProfile as any)?.cupSize as string | undefined;
    const r = ((activeProfile as any)?.braSizeRegion as string | undefined)
      ?? ((activeProfile as any)?.braRegion as string | undefined);
    if (b) setBandSize(b);
    if (c) setCupSize(c);
    if (r) setBraSizeRegion(r);
  }, [activeProfile]);
  const [braRegionOpen, setBraRegionOpen] = useState(false);
  // Tracks which inline mini-select on the photo step is open (region | band | cup | null).
  // Single state so opening one closes the others. Click-outside closes via effect below.
  const [inlineBraOpen, setInlineBraOpen] = useState<"region" | "band" | "cup" | null>(null);
  const inlineBraWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!inlineBraOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const wrap = inlineBraWrapRef.current;
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      if (wrap && (path.includes(wrap) || wrap.contains(e.target as Node))) return;
      setInlineBraOpen(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [inlineBraOpen]);
  const bandOptions = BAND_SIZES[braSizeRegion] || BAND_SIZES.US;
  const [shoeBrandId, setShoeBrandId] = useState(() => formRef.current.referenceShoeBrandId || "");
  const [shoeSizeSystem, setShoeSizeSystem] = useState<ShoeSizeSystem>(() => {
    const saved = formRef.current.referenceShoeSizeSystem as ShoeSizeSystem | undefined;
    return saved || getDefaultShoeSizeSystem(sizingCountry);
  });
  const [shoeSizeValue, setShoeSizeValue] = useState(() => formRef.current.referenceShoeSizeValue || formRef.current.referenceShoeSizeUS || "");
  const [shoeLengthUnit, setShoeLengthUnit] = useState<"cm" | "in">(() => sizingUnit === "in" ? "in" : "cm");
  const [photoDetectedGender, setPhotoDetectedGender] = useState<ShoeGender | null>(() => {
    const saved = formRef.current.detectedGender || formRef.current.gender;
    return saved === "male" || saved === "female" ? saved : null;
  });
  const fallbackGender: ShoeGender = isWomen ? "female" : "male";
  const gender: ShoeGender = photoDetectedGender || fallbackGender;
  const shoeGender: ShoeGender = photoDetectedGender || inferShoeGender(productTitle, isWomen);
  const shoeSizeOptions = getShoeSizeOptions(shoeSizeSystem);
  const shoeReference = isShoeReferenceMode
    ? estimateShoeReference({ brandId: shoeBrandId, size: shoeSizeValue, sizeSystem: shoeSizeSystem, productTitle, gender: shoeGender })
    : null;
  const selectedShoeBrand = SHOE_BRANDS.find((brand) => brand.id === shoeBrandId);

  const seededBasicsProfileIdRef = useRef<string | null>(null);
  useEffect(() => {
    const profileId = (activeProfile as any)?.id ?? null;
    if (!profileId || seededBasicsProfileIdRef.current === profileId) return;
    seededBasicsProfileIdRef.current = profileId;

    const nextProfileHeight = activeProfile?.height ?? (activeProfile as any)?.heightCm;
    const nextProfileWeight = activeProfile?.weight ?? (activeProfile as any)?.weightKg;
    const nextProfileAge = activeProfile?.age;
    const nextProfileHeightUnit =
      (activeProfile?.heightUnit as string | undefined) === "ft"
        ? "in"
        : ((activeProfile?.heightUnit as string | undefined) || ((activeProfile as any)?.heightCm ? "cm" : undefined));
    const nextProfileWeightUnit =
      (activeProfile?.weightUnit as string | undefined) || ((activeProfile as any)?.weightKg ? "kg" : undefined);

    if (nextProfileHeight != null && Number(nextProfileHeight) > 0) {
      if (nextProfileHeightUnit === "cm") {
        const cm = Math.round(Number(nextProfileHeight));
        const totalIn = Math.round(cm / 2.54);
        setHeight(String(cm));
        setHeightFeet(String(Math.floor(totalIn / 12)));
        setHeightInches(String(totalIn % 12));
        setPhotoStepHeight(String(cm));
        setHUnit("cm");
        setHeightUnit("cm");
      } else {
        const totalIn = Math.round(Number(nextProfileHeight));
        setHeightFeet(String(Math.floor(totalIn / 12)));
        setHeightInches(String(totalIn % 12));
        setHeight(String(Math.round(totalIn * 2.54)));
        setPhotoStepHeight(String(Math.round(totalIn * 2.54)));
        setHUnit("in");
        setHeightUnit("in");
      }
    }

    if (nextProfileWeight != null && Number(nextProfileWeight) > 0) {
      setWeight(String(Math.round(Number(nextProfileWeight))));
      if (nextProfileWeightUnit) {
        setWUnit(nextProfileWeightUnit);
        setWeightUnit(nextProfileWeightUnit);
      }
    }

    if (nextProfileAge != null && Number(nextProfileAge) > 0) {
      setAge(String(Math.round(Number(nextProfileAge))));
    }
  }, [activeProfile, setHeightUnit, setWeightUnit]);

  // Get illustrations based on gender
  const illustrations = showGenderSpecificDetails ? BODY_ILLUSTRATIONS.female : BODY_ILLUSTRATIONS.male;

  // Validation
  const [error, setError] = useState("");

  const handlePhotoSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (ageConfirmed !== true) {
      setError(t("Please confirm that the person in the photo is 18 or older before uploading."));
      return;
    }
    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError(t("Please upload an image file"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t("Image must be under 10MB"));
      return;
    }
    setError("");
    setPhotoProcessing(true);
    setPhotoStatus(t("Analyzing photo…"));
    setPhotoFile(file);
    setPhotoHelpOpen(false);

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPhotoPreview(previewUrl);

    try {
      // Run age-check (small thumb) and main compression (1024px) in
      // parallel so user-perceived latency is bounded by whichever finishes
      // last, not the sum. checkAgeBeforeUpload internally compresses the
      // File to a 384px thumb — much smaller payload to Gemini Flash-Lite
      // than the full 1024px image we keep for the actual measurement run.
      const ageCheckPromise: Promise<{ isAdult: boolean; confidence: "high" | "low"; reasoning?: string; detectedGender?: "male" | "female" | "unknown" }> =
        apiUrl && apiKey
          ? checkAgeBeforeUpload(file, apiUrl, apiKey)
          : Promise.resolve({ isAdult: true, confidence: "low" });
      const compressPromise = compressImage(file, { maxDimension: 1024, quality: 0.85 });

      const ageResult = await ageCheckPromise;
      if (!ageResult.isAdult) {
        // Set photoRejection (not setError) — UI shows the rejection reason
        // in the same spot as the "Are you 18+?" prompt instead of a generic
        // toast. Falls back to a polite default if no reasoning provided.
        const reason = ageResult.reasoning?.trim() || t("This photo appears to be of a minor. Please upload a photo of someone 18 or older.");
        setPhotoRejection(reason);
        setPhotoPreview(null);
        setPhotoFile(null);
        return; // do NOT call setPhotoBase64 — image is dropped here, never sent further
      }
      if (ageResult.detectedGender === "male" || ageResult.detectedGender === "female") {
        setPhotoDetectedGender(ageResult.detectedGender);
        formRef.current.detectedGender = ageResult.detectedGender;
        formRef.current.gender = ageResult.detectedGender;
      }

      const base64 = await compressPromise;
      setPhotoBase64(base64);
      setPhotoRejection(null);
    } catch {
      setError(t("Failed to process image"));
      setPhotoPreview(null);
    } finally {
      setPhotoProcessing(false);
      setPhotoStatus("");
    }
  }, [t, ageConfirmed, apiUrl, apiKey, formRef]);

  // Track which profile id we've already seeded the photo from. Once set,
  // never re-seed for that id — otherwise clearing the photo immediately
  // re-runs the preload effect and undoes the clear.
  const seededProfileIdRef = useRef<string | null>(null);

  const handleRemovePhoto = useCallback(() => {
    if (photoPreview && !photoFromProfile) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoBase64(null);
    setPhotoFromProfile(false);
    setPhotoDetectedGender(null);
    delete formRef.current.detectedGender;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [photoPreview, photoFromProfile, formRef]);

  // Pill "Clear" button — wipes EVERYTHING that came pre-filled from the
  // active profile: photo + height/weight/age + women's bra size, AND
  // dismisses the active profile so the "Using X · start fresh" hint
  // disappears. The photo's own × button uses handleRemovePhoto and
  // leaves both the numbers and the active profile alone (different
  // intent: "I want to upload a different photo").
  const handleClearFromProfile = useCallback(() => {
    handleRemovePhoto();
    setHeight("");
    setHeightFeet("");
    setHeightInches("");
    setWeight("");
    setAge("");
    setBandSize(null);
    setCupSize(null);
    onStartFresh?.();
  }, [handleRemovePhoto, onStartFresh]);

  // Preload the photo from the active profile so the user can see what was
  // saved when they re-open the SDK. Seeds at most once per profile id; the
  // ref guard means clearing the photo (or uploading a new one) doesn't
  // bounce the saved photo back in.
  useEffect(() => {
    const profilePhoto = (activeProfile as any)?.photoUrl || (activeProfile as any)?.photoBase64;
    const profileId = (activeProfile as any)?.id ?? null;
    if (!profilePhoto || !profileId) return;
    if (seededProfileIdRef.current === profileId) return;
    seededProfileIdRef.current = profileId;
    let cancelled = false;
    const source = normalizeProfilePhotoSource(profilePhoto);
    if (source) {
      setPhotoBase64(source);
      setPhotoPreview(source);
    }
    setPhotoFromProfile(true);
    // Skip the age gate — the photo already cleared it when the profile
    // was created. Without this the user sees the loaded photo behind the
    // Yes/No prompt and can't proceed.
    setAgeConfirmed(true);

    profilePhotoToDataUrl(profilePhoto)
      .then(async (dataUrl) => {
        const blob = await profilePhotoToBlob(dataUrl);
        if (cancelled) return;
        setPhotoBase64(dataUrl);
        setPhotoPreview(dataUrl);
        setPhotoFile(new File([blob], "profile-photo.jpg", { type: blob.type || "image/jpeg" }));
        setPhotoRejection(null);
        setError("");
      })
      .catch(() => {
        if (!cancelled && source) {
          setPhotoBase64(source);
          setPhotoPreview(source);
          setPhotoFile(null);
        }
      });

    return () => { cancelled = true; };
  }, [activeProfile]);

  // Top-up sync: if the active profile gains an age after this view
  // mounted (e.g. BG vision estimation patches userEstimates.age onto the
  // profile after the wizard closes), reflect it on the manual basics
  // AGE input. Guarded by `!age` so it never overwrites user input.
  useEffect(() => {
    const profileAgeNow = (activeProfile as any)?.age;
    if (profileAgeNow != null && profileAgeNow > 0 && !age) {
      setAge(String(Math.round(profileAgeNow)));
    }
  }, [activeProfile, age]);

  const validateBasics = useCallback(() => {
    if (hUnit === "in") {
      const ft = parseFloat(heightFeet);
      if (!ft || ft < 3 || ft > 8) { setError(t("Please enter a valid height")); return false; }
    } else {
      const h = parseFloat(height);
      if (!h || h < 100 || h > 250) { setError(t("Please enter a valid height (100-250 cm)")); return false; }
    }
    const w = parseFloat(weight);
    if (wUnit === "lbs") {
      if (!w || w < 66 || w > 660) { setError(t("Please enter a valid weight")); return false; }
    } else {
      if (!w || w < 30 || w > 300) { setError(t("Please enter a valid weight (30-300 kg)")); return false; }
    }
    setError("");
    return true;
  }, [hUnit, wUnit, height, heightFeet, weight, t]);

  // Helper: get raw height/weight values in whatever unit the user chose — NO conversion
  const getValues = () => {
    let heightVal: number;
    if (hUnit === "in") {
      const ft = parseFloat(heightFeet) || 0;
      const inc = parseFloat(heightInches) || 0;
      heightVal = ft * 12 + inc; // total inches, raw
    } else {
      heightVal = parseFloat(height); // raw cm
    }
    return { heightVal, weightVal: parseFloat(weight) }; // raw lbs or kg
  };

  // Next step logic
  const nextStep = (current: Step): Step | "submit" => {
    const idx = steps.indexOf(current);
    return idx < steps.length - 1 ? steps[idx + 1] : "submit";
  };

  const submitProfile = () => {
    const { heightVal, weightVal } = getValues();
    formRef.current.height = String(heightVal);
    formRef.current.weight = String(weightVal);
    formRef.current.gender = gender;
    if (age) formRef.current.age = age;
    if (chestProfile) formRef.current.chestProfile = chestProfile;
    if (midsectionProfile) formRef.current.midsectionProfile = midsectionProfile;
    if (hipProfile) formRef.current.hipProfile = hipProfile;
    if (bandSize) formRef.current.bandSize = bandSize;
    if (cupSize) formRef.current.cupSize = cupSize;
    if (braSizeRegion) formRef.current.braSizeRegion = braSizeRegion;
    setHeightUnit(hUnit);
    setWeightUnit(wUnit);
    onComplete({
      height: heightVal, weight: weightVal, heightUnit: hUnit, weightUnit: wUnit, gender,
      age: age ? parseInt(age, 10) : undefined,
      chestProfile: chestProfile || undefined,
      midsectionProfile: midsectionProfile || undefined,
      hipProfile: hipProfile || undefined,
      bodyImage: photoBase64 || undefined,
      bandSize: bandSize || undefined,
      cupSize: cupSize || undefined,
      braSizeRegion: bandSize && cupSize ? braSizeRegion : undefined,
    });
  };

  // Helper that performs the actual snap submit using the current form state.
  // Used by both desktop's single-step photo flow and mobile Step 2.
  const submitSnap = useCallback(async () => {
    if (!photoBase64 || !photoFile) return;
    const currentShoeReference = isShoeReferenceMode
      ? estimateShoeReference({ brandId: shoeBrandId, size: shoeSizeValue, sizeSystem: shoeSizeSystem, productTitle, gender: shoeGender })
      : null;
    if (isShoeReferenceMode && !currentShoeReference) {
      setError(t("Please select your usual shoe brand and size"));
      return;
    }
    if (currentShoeReference) {
      formRef.current.referenceShoeBrandId = currentShoeReference.brandId;
      formRef.current.referenceShoeBrand = currentShoeReference.brandLabel;
      formRef.current.referenceShoeSizeValue = currentShoeReference.selectedSize;
      formRef.current.referenceShoeSizeSystem = currentShoeReference.selectedSizeSystem;
      formRef.current.referenceShoeGender = currentShoeReference.gender;
      formRef.current.footLengthCm = String(currentShoeReference.footLengthCm);
      delete formRef.current.referenceShoeSizeUS;
      delete formRef.current.shoeUS;
      delete formRef.current.shoeUK;
      delete formRef.current.shoeEU;
      formRef.current.sizingUnit = "cm";
      formRef.current.heightUnit = "cm";
      formRef.current.weightUnit = "kg";
      setSizingUnit?.("cm");
    }
    const heightForSubmit = isShoeReferenceMode ? 170 : hUnit === "in"
      ? (parseFloat(heightFeet) || 0) * 12 + (parseFloat(heightInches) || 0)
      : parseFloat(height) || 0;
    const weightForSubmit = isShoeReferenceMode ? 70 : parseFloat(weight) || 0;
    const ageForSubmit = parseFloat(age) || undefined;
    const styleMatchSelections = step === "style-preview"
      ? selectedStyleRowsForTryOn(stylePreviewRows, apiUrl).map((selection) =>
        prepareStyleMatchSelectionForTryOn(selection, apiUrl),
      )
      : [];
    onSnapSubmit({
      photoFile,
      photoBase64,
      height: heightForSubmit,
      weight: weightForSubmit,
      heightUnit: isShoeReferenceMode ? "cm" : hUnit,
      weightUnit: isShoeReferenceMode ? "kg" : wUnit,
      sizingUnit: isShoeReferenceMode ? "cm" : (sizingUnit === "in" ? "in" : hUnit === "in" ? "in" : "cm"),
      gender,
      ...(ageForSubmit ? { age: ageForSubmit } : {}),
      ...(chestProfile ? { chestProfile } : {}),
      ...(midsectionProfile ? { midsectionProfile } : {}),
      ...(hipProfile ? { hipProfile } : {}),
      ...(styleMatchSelections.length ? { styleMatchSelections } : {}),
      ...(currentShoeReference ? {
        extraMeasurements: {
          referenceShoeBrandId: currentShoeReference.brandId,
          referenceShoeBrand: currentShoeReference.brandLabel,
          referenceShoeSizeValue: currentShoeReference.selectedSize,
          referenceShoeSizeSystem: currentShoeReference.selectedSizeSystem,
          referenceShoeGender: currentShoeReference.gender,
          referenceFootLengthCm: currentShoeReference.footLengthCm,
          footLengthCm: currentShoeReference.footLengthCm,
        },
      } : {}),
      ...(showGenderSpecificDetails && bandSize && cupSize ? {
        bandSize,
        cupSize,
        braSizeRegion,
        braSize: {
          band: parseFloat(bandSize),
          cup: cupSize,
          region: braSizeRegion ?? "US",
        },
      } : {}),
    });
  }, [photoBase64, photoFile, isShoeReferenceMode, shoeBrandId, shoeSizeValue, shoeSizeSystem, productTitle, shoeGender, formRef, setSizingUnit, sizingUnit, hUnit, wUnit, heightFeet, heightInches, height, weight, age, gender, showGenderSpecificDetails, bandSize, cupSize, braSizeRegion, step, stylePreviewRows, onSnapSubmit, apiKey, apiUrl, t]);

  const continueWithoutStylePreview = useCallback(() => {
    setStylePreviewRows([]);
    setStylePreviewOutfits([]);
    setStylePreviewOutfitIndex(0);
    setStylePreviewHangerRowId(null);
    setStylePreviewLoading(false);
    setStylePreviewMessage(null);

    if (photoBase64 && photoFile) {
      submitSnap();
      return;
    }
    if (hasActiveProfileWithMeasurements && onUseActiveProfile) {
      onUseActiveProfile(undefined, hUnit === "in" ? "in" : "cm");
      return;
    }
    submitProfile();
  }, [hUnit, hasActiveProfileWithMeasurements, onUseActiveProfile, photoBase64, photoFile, submitSnap, submitProfile]);

  const toggleStylePreviewRow = useCallback((id: string) => {
    setStylePreviewRows((rows) => {
      const nextRows = rows.map((row) => row.id === id ? { ...row, selected: !row.selected } : row);
      setStylePreviewOutfits((outfits) =>
        outfits.map((outfit, index) => index === stylePreviewOutfitIndex ? { ...outfit, rows: nextRows } : outfit),
      );
      return nextRows;
    });
  }, [stylePreviewOutfitIndex]);

  const selectStylePreviewOption = useCallback((id: string, optionIndex: number) => {
    setStylePreviewHangerRowId(id);
    setStylePreviewRows((rows) => {
      const nextRows = rows.map((row) => {
        if (row.id !== id || !row.options?.[optionIndex]) return row;
        return {
          ...row,
          ...row.options[optionIndex],
          optionIndex,
          selected: true,
        };
      });
      setStylePreviewOutfits((outfits) =>
        outfits.map((outfit, index) => index === stylePreviewOutfitIndex ? { ...outfit, rows: nextRows } : outfit),
      );
      return nextRows;
    });
  }, [stylePreviewOutfitIndex]);

  const selectStylePreviewOutfit = useCallback((index: number) => {
    const nextRows = stylePreviewOutfits[index]?.rows || [];
    if (!nextRows.length) return;
    setStylePreviewOutfitIndex(index);
    setStylePreviewRows(nextRows);
    setStylePreviewHangerRowId(nextRows[0]?.id ?? null);
  }, [stylePreviewOutfits]);

  const applyInstantOutfitLooks = useCallback((looks: PrimeStyleOutfitLook[] | undefined) => {
    setStylePreviewRows([]);
    setStylePreviewOutfits([]);
    setStylePreviewOutfitIndex(0);
    setStylePreviewHangerRowId(null);
    setStylePreviewMessage(null);
    if (looks === undefined) {
      setStylePreviewLoading(true);
      return;
    }
    const outfits = outfitsFromStyleMatchResponse(looks, currentRecommendedSize);
    if (!outfits.length) {
      setStylePreviewLoading(false);
      setStylePreviewMessage(t("No matching outfit products were found."));
      return;
    }
    setStylePreviewOutfits(outfits);
    setStylePreviewRows(outfits[0].rows);
    setStylePreviewHangerRowId(outfits[0].rows[0]?.id ?? null);
    setStylePreviewLoading(false);
  }, [currentRecommendedSize, t]);

  const openStylePreview = useCallback(() => {
    // Outfit Builder is intentionally unavailable on footwear PDPs. Shoe
    // sizing has its own reference flow and must never be routed into apparel
    // outfit matching, even when a host supplies AI Stylist looks.
    if (isShoeReferenceMode) return;
    if (outfitBuilderSource === "ai-stylist") {
      setStep("style-preview");
      applyInstantOutfitLooks(instantOutfitLooks);
      return;
    }
    if (!apiKey) {
      continueWithoutStylePreview();
      return;
    }
    if (stylePreviewRequestInFlightRef.current) return;
    stylePreviewRequestInFlightRef.current = true;

    setStylePreviewRows([]);
    setStylePreviewOutfits([]);
    setStylePreviewOutfitIndex(0);
    setStylePreviewHangerRowId(null);
    setStylePreviewMessage(null);
    setStylePreviewLoading(true);
    // Enter the existing Outfit Builder immediately so the user sees its
    // loading screen while recommendations are being prepared.
    setStep("style-preview");
    const client = new ApiClient(apiKey, apiUrl);
    const product: StyleMatchRequestProduct = styleMatchProduct || {
      title: productTitle,
      image: productImage,
      images: productImages,
    };
    const measurements: Record<string, unknown> = {
      height: hUnit === "in" ? (parseFloat(heightFeet) || 0) * 12 + (parseFloat(heightInches) || 0) : parseFloat(height) || null,
      weight: parseFloat(weight) || null,
      heightUnit: hUnit,
      weightUnit: wUnit,
    };
    if (bandSize) measurements.bandSize = bandSize;
    if (cupSize) measurements.cupSize = cupSize;
    if (braSizeRegion) measurements.braSizeRegion = braSizeRegion;

    client.getStyleMatches({
      product,
      profile: {
        gender,
        recommendedSize: currentRecommendedSize || undefined,
        measurements,
      },
      personImage: photoBase64 || undefined,
      limitOutfits: 10,
      optionsPerSlot: 8,
    })
      .then((response) => {
        if (response.source === "disabled" || response.enabled === false) {
          setStylePreviewAvailability("disabled");
          continueWithoutStylePreview();
          return;
        }
        if (response.source === "person_image_required" || response.errorCode === "person_image_required") {
          continueWithoutStylePreview();
          return;
        }
        const outfits = outfitsFromStyleMatchResponse(response.outfits, currentRecommendedSize);
        if (outfits.length) {
          setStylePreviewOutfits(outfits);
          setStylePreviewOutfitIndex(0);
          setStylePreviewRows(outfits[0].rows);
          setStylePreviewHangerRowId(outfits[0].rows[0]?.id ?? null);
          setStylePreviewMessage(null);
          setStylePreviewLoading(false);
        } else {
          setStylePreviewMessage(t("No matching outfit products were found."));
        }
      })
      .catch(() => {
        setStylePreviewMessage(t("Couldn't load outfit suggestions. Try again."));
      })
      .finally(() => {
        stylePreviewRequestInFlightRef.current = false;
        setStylePreviewLoading(false);
      });
  }, [
    applyInstantOutfitLooks,
    apiKey,
    apiUrl,
    bandSize,
    braSizeRegion,
    cupSize,
    currentRecommendedSize,
    continueWithoutStylePreview,
    gender,
    hUnit,
    height,
    heightFeet,
    heightInches,
    productImage,
    productImages,
    productTitle,
    outfitBuilderSource,
    instantOutfitLooks,
    isShoeReferenceMode,
    photoBase64,
    styleMatchProduct,
    t,
    wUnit,
    weight,
  ]);

  useEffect(() => {
    if (outfitBuilderSource !== "ai-stylist" || step !== "style-preview") return;
    applyInstantOutfitLooks(instantOutfitLooks);
  }, [applyInstantOutfitLooks, instantOutfitLooks, outfitBuilderSource, step]);

  useEffect(() => {
    if (isShoeReferenceMode || !instantOutfitLooks?.length || typeof Image === "undefined") return;

    // Warm only the first visible look (plus its alternatives) as soon as the
    // host supplies it. This removes the blank-card delay without downloading
    // every image from all ten looks or blocking the photo/details first step.
    const urls = new Set<string>();
    for (const item of instantOutfitLooks[0]?.items || []) {
      if (item.displayImage || item.image) urls.add(item.displayImage || item.image);
      for (const alternative of item.alternatives || []) {
        if (urls.size >= 16) break;
        if (alternative.displayImage || alternative.image) {
          urls.add(alternative.displayImage || alternative.image);
        }
      }
      if (urls.size >= 16) break;
    }

    for (const url of urls) {
      const image = new Image();
      image.decoding = "async";
      image.src = url;
      void image.decode?.().catch(() => undefined);
    }
  }, [instantOutfitLooks, isShoeReferenceMode]);

  const handleNext = useCallback(async () => {
    if (step === "basics") {
      if (hasActiveProfileWithMeasurements && onUseActiveProfile) {
        if (shouldOfferStylePreview) {
          openStylePreview();
          return;
        }
        onUseActiveProfile(undefined, hUnit === "in" ? "in" : "cm");
        return;
      }
      if (!validateBasics()) return;
      const next = nextStep("basics");
      if (next === "submit") submitProfile();
      else setStep(next as Step);
    } else if (step === "chest") {
      if (!chestProfile) return;
      setStep(nextStep("chest") as Step);
    } else if (step === "midsection") {
      if (!midsectionProfile) return;
      setStep(nextStep("midsection") as Step);
    } else if (step === "seat") {
      if (!seatProfile) return;
      const next = nextStep("seat");
      if (next === "submit") { submitProfile(); } else { setStep(next as Step); }
    } else if (step === "hips") {
      if (!hipProfile) return;
      const next = nextStep("hips");
      if (next === "submit") { submitProfile(); } else { setStep(next as Step); }
    } else if (step === "bra") {
      if (!bandSize || !cupSize) { setError(t("Please select both band and cup size")); return; }
      setError("");
      submitProfile();
    } else if (step === "photo") {
      // Mobile: Step 1 of 2 (photo upload) — validate photo + age, then
      // advance to Step 2 (scan-details) for height/weight/bra.
      // Desktop: single-screen flow, validate everything and submit.
      if (!photoBase64 || !photoFile) {
        if (hasActiveProfileWithMeasurements && onUseActiveProfile) {
          if (shouldOfferStylePreview) {
            openStylePreview();
            return;
          }
          onUseActiveProfile(undefined, hUnit === "in" ? "in" : "cm");
          return;
        }
        setError(t("Please upload a photo"));
        return;
      }
      if (ageConfirmed !== true) {
        setError(t("Please confirm your age"));
        return;
      }
      setError("");
      if (isMobile) { setStep("scan-details"); return; }
      if (isShoeReferenceMode && !shoeReference) {
        setError(t("Please select your usual shoe brand and size"));
        return;
      }
      if (shouldOfferStylePreview) {
        openStylePreview();
        return;
      }
      submitSnap();
    } else if (step === "style-preview") {
      if (stylePreviewSubmitInFlightRef.current) return;
      stylePreviewSubmitInFlightRef.current = true;
      setStylePreviewSubmitting(true);
      setError("");
      if (!photoBase64 || !photoFile) {
        if (hasActiveProfileWithMeasurements && onUseActiveProfile) {
          const selections = selectedStyleRowsForTryOn(stylePreviewRows, apiUrl).map((selection) =>
            prepareStyleMatchSelectionForTryOn(selection, apiUrl),
          );
          onUseActiveProfile(selections, hUnit === "in" ? "in" : "cm");
          return;
        }
        submitProfile();
        return;
      }
      submitSnap();
    } else if (step === "scan-details") {
      if (isShoeReferenceMode) {
        if (!shoeReference) {
          setError(t("Please select your usual shoe brand and size"));
          return;
        }
        setError("");
        submitSnap();
        return;
      }
      // Mobile Step 2: validate body inputs and submit.
      const heightFilled = hUnit === "in"
        ? parseFloat(heightFeet) > 0 || parseFloat(heightInches) > 0
        : parseFloat(height) > 0;
      const weightFilled = parseFloat(weight) > 0;
      if (!heightFilled) { setError(t("Please enter your height")); return; }
      if (!weightFilled) { setError(t("Please enter your weight")); return; }
      if (showGenderSpecificDetails && (!bandSize || !cupSize)) { setError(t("Please select both band and cup size")); return; }
      if (!photoBase64 || !photoFile) { setError(t("Please upload a photo")); return; }
      setError("");
      if (shouldOfferStylePreview) {
        openStylePreview();
        return;
      }
      submitSnap();
    }
  }, [step, steps, validateBasics, chestProfile, midsectionProfile, hipProfile, seatProfile, bandSize, cupSize, hUnit, wUnit, height, heightFeet, heightInches, weight, age, photoBase64, photoFile, ageConfirmed, isMobile, showGenderSpecificDetails, isShoeReferenceMode, shoeReference, gender, hasActiveProfileWithMeasurements, onUseActiveProfile, shouldOfferStylePreview, openStylePreview, submitSnap, apiKey, apiUrl, t]);

  // Previous step
  const prevStep = (current: Step): Step | null => {
    const idx = steps.indexOf(current);
    return idx > 0 ? steps[idx - 1] : null;
  };

  const handleBackStep = useCallback(() => {
    setError("");
    if (step === "basics") {
      onBack();
    } else if (step === "photo") {
      if (simplePhotoOnly) {
        onBack();
        return;
      }
      // Mobile Step 1 — exit to manual basics so the user can switch tabs;
      // desktop has been on the photo screen from scratch, so basics too.
      setStep("basics");
    } else if (step === "scan-details") {
      // Mobile Step 2 — back to Step 1 (photo).
      setStep("photo");
    } else if (step === "style-preview") {
      setStep(isMobile ? "scan-details" : "photo");
    } else {
      const prev = prevStep(step);
      if (prev) setStep(prev);
      else onBack();
    }
  }, [step, steps, onBack, simplePhotoOnly]);

  const canProceed =
    step === "basics" ? true :
    step === "scan-details" ? true :
    step === "style-preview" ? true :
    step === "chest" ? !!chestProfile :
    step === "midsection" ? !!midsectionProfile :
    step === "seat" ? !!seatProfile :
    step === "hips" ? !!hipProfile :
    step === "bra" ? !!(bandSize && cupSize) :
    step === "photo" ? true : false;

  const isLastStep = step === steps[steps.length - 1];

  // Shared unit-toggle helpers — used by both the scan-details step (Step 1)
  // and the photo step (Step 2) on mobile so unit switching preserves the
  // current numeric values.
  const photoSwitchToMetric = () => {
    if (hUnit === "in") {
      const totalIn = (parseFloat(heightFeet) || 0) * 12 + (parseFloat(heightInches) || 0);
      if (totalIn > 0) setHeight(String(Math.round(totalIn * 2.54)));
    }
    if (wUnit === "lbs" && weight) setWeight(String(Math.round(parseFloat(weight) / 2.205)));
    setHUnit("cm"); setWUnit("kg");
    setHeightUnit("cm"); setWeightUnit("kg"); setSizingUnit?.("cm");
  };
  const photoSwitchToImperial = () => {
    if (hUnit === "cm") {
      const cmVal = parseFloat(height);
      if (cmVal > 0) {
        const totalIn = Math.round(cmVal / 2.54);
        setHeightFeet(String(Math.floor(totalIn / 12)));
        setHeightInches(String(totalIn % 12));
      }
    }
    if (wUnit === "kg" && weight) setWeight(String(Math.round(parseFloat(weight) * 2.205)));
    setHUnit("in"); setWUnit("lbs");
    setHeightUnit("in"); setWeightUnit("lbs"); setSizingUnit?.("in");
  };

  const renderShoeReferenceFields = () => (
    <div className={`ps-shoe-ref${selectedShoeBrand ? " ps-size-mode" : " ps-brand-mode"}`}>
      <div className={`ps-shoe-stage ps-shoe-brand-stage${selectedShoeBrand ? " ps-hidden" : " ps-active"}`}>
        <div className="ps-shoe-ref-head">
          <span className="ps-shoe-ref-kicker">{t("Step 01")}</span>
          <h3 className="ps-shoe-ref-title">{t("Which shoe brand do you usually wear?")}</h3>
          <p className="ps-shoe-ref-copy">{t("Pick a brand you know well. We'll use that size as your fit reference.")}</p>
        </div>

        <div className="ps-shoe-brand-grid" role="listbox" aria-label={t("Shoe brand")}>
          {SHOE_BRANDS.map((brand) => {
            const selected = brand.id === shoeBrandId;
            return (
              <button
                key={brand.id}
                type="button"
                className={`ps-shoe-brand-card${selected ? " ps-selected" : ""}`}
                onClick={() => {
                  setShoeBrandId(brand.id);
                  setShoeSizeValue("");
                  setError("");
                }}
                aria-pressed={selected}
              >
                <span className="ps-shoe-brand-logo">
                  <img className="ps-shoe-brand-logo-img" src={brand.logoSrc} alt="" aria-hidden="true" />
                </span>
                <span className="ps-shoe-brand-name">{brand.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={`ps-shoe-stage ps-shoe-size-stage${selectedShoeBrand ? " ps-active" : " ps-hidden"}`}>
        <div className="ps-shoe-size-toolbar">
          <button
            type="button"
            className="ps-shoe-back-brand"
            onClick={() => {
              setShoeBrandId("");
              setShoeSizeValue("");
              setError("");
            }}
          >
            <span aria-hidden="true">←</span>
            {t("Back to brands")}
          </button>

          <div className="ps-shoe-size-controls">
            <label className="ps-shoe-country-select">
              <span>{t("Country")}</span>
              <select
                value={shoeSizeSystem}
                onChange={(event) => {
                  setShoeSizeSystem(event.target.value as ShoeSizeSystem);
                  setShoeSizeValue("");
                  setError("");
                }}
              >
                {SHOE_SIZE_SYSTEM_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <div className="ps-shoe-unit-toggle" aria-label={t("Foot length unit")}>
              {(["cm", "in"] as const).map((unit) => (
                <button
                  key={unit}
                  type="button"
                  className={shoeLengthUnit === unit ? "ps-active" : ""}
                  onClick={() => setShoeLengthUnit(unit)}
                >
                  {unit}
                </button>
              ))}
            </div>
          </div>
        </div>

        {selectedShoeBrand && (
          <div className="ps-shoe-selected-brand">
            <span className="ps-shoe-selected-logo">
              <img className="ps-shoe-brand-logo-img" src={selectedShoeBrand.logoSrc} alt="" aria-hidden="true" />
            </span>
            <span>
              <small>{t("Selected brand")}</small>
              <strong>{selectedShoeBrand.label}</strong>
            </span>
            <button
              type="button"
              className="ps-shoe-change-brand"
              onClick={() => {
                setShoeBrandId("");
                setShoeSizeValue("");
                setError("");
              }}
            >
              {t("Change")}
            </button>
          </div>
        )}

        <div className="ps-shoe-size-panel ps-enabled">
          <div className="ps-shoe-size-head">
            <span className="ps-shoe-ref-kicker">{t("Step 02")}</span>
            <h3 className="ps-shoe-ref-title">
              {`${t("Choose your usual")} ${shoeSizeSystem} ${t("size")}`}
            </h3>
          </div>
          <div className="ps-shoe-size-grid" role="listbox" aria-label={t("Shoe size")}>
            {shoeSizeOptions.map((size) => {
              const selected = shoeSizeValue === size;
              return (
                <button
                  key={`${shoeSizeSystem}-${size}`}
                  type="button"
                  className={`ps-shoe-size-chip${selected ? " ps-selected" : ""}`}
                  onClick={() => {
                    setShoeSizeValue(size);
                    setError("");
                  }}
                  aria-pressed={selected}
                >
                  {size}
                </button>
              );
            })}
          </div>
        </div>

        {shoeReference && (
          <div className="ps-shoe-ref-summary">
            <span>{t("Fit reference")}</span>
            <strong>{shoeReference.brandLabel} {shoeReference.selectedSizeSystem} {shoeReference.selectedSize}</strong>
            <small>{t("Foot length")} {formatShoeFootLength(shoeReference.footLengthCm, shoeLengthUnit)}</small>
          </div>
        )}
      </div>
    </div>
  );

  if (step === "style-preview") {
    const subjectImage = photoBase64 || productImage;
    const selectedStyleRows = stylePreviewRows.filter((row) => row.selected);
    const selectedItemCount = selectedStyleRows.length + 1;
    const outfitCount = Math.max(stylePreviewOutfits.length, 1);

    return (
      <div className="ps-bp-wrapper ps-bp-style-wrapper ps-bp-fitting-room">
        <main className="ps-bp-fitting-shell">
          <header className="ps-bp-fitting-topbar">
            <button type="button" className="ps-bp-fitting-back" onClick={handleBackStep} aria-label={t("Back")}>
              <ArrowLeftIcon />
            </button>
            <div>
              <h2>{t("Complete Your Look")}</h2>
              <p>{t("Build the perfect outfit around your pinned piece.")}</p>
            </div>
          </header>

          <div className="ps-bp-outfit-grid">
            <section className="ps-bp-outfit-preview" aria-label={t("Pinned outfit preview")}>
              <img className="ps-bp-fitting-person" src={subjectImage} alt={productTitle} draggable={false} decoding="async" fetchPriority="high" />
              <article className="ps-bp-outfit-anchor">
                <span className="ps-bp-outfit-anchor-image">
                  <StylePreviewGarmentImage src={productImage} alt={productTitle} apiKey={apiKey} apiUrl={apiUrl} eager />
                </span>
                <span className="ps-bp-outfit-anchor-copy">
                  <span className="ps-bp-outfit-pinned"><SparkleIcon size={13} /> {t("Pinned")}</span>
                  <strong>{productTitle}</strong>
                  {currentRecommendedSize && <small>{t("Size")}: {currentRecommendedSize}</small>}
                </span>
                <span className="ps-bp-outfit-anchor-note">{t("This is your anchor piece. All looks are built around it.")}</span>
              </article>
            </section>

            {stylePreviewLoading ? (
              <section className="ps-bp-outfit-loading-panel">
                <div className="ps-bp-style-loader" role="status" aria-live="polite">
                  <span className="ps-bp-style-loader-kicker">{t("Complete Your Style")}</span>
                  <span className="ps-bp-style-loader-title">{t("Finding matching products...")}</span>
                  <span className="ps-bp-style-loader-line" aria-hidden="true" />
                </div>
              </section>
            ) : stylePreviewMessage ? (
              <section className="ps-bp-outfit-loading-panel">
                <div className="ps-bp-style-loader ps-bp-style-loader-muted" role="status">
                  <span className="ps-bp-style-loader-kicker">{t("Complete Your Style")}</span>
                  <span className="ps-bp-style-loader-title">{stylePreviewMessage}</span>
                  {error && <p className="ps-bpm-error">{error}</p>}
                  <button type="button" className="ps-bp-fitting-retry" onClick={openStylePreview}>
                    {t("Try Again")}
                  </button>
                </div>
              </section>
            ) : (
              <>
                <section className="ps-bp-outfit-builder" aria-label={t("Choose outfit pieces")}>
                  <div className="ps-bp-outfit-columns">
                    {stylePreviewRows.map((row) => {
                      const options = row.options?.length ? row.options : [row];
                      return (
                        <section className="ps-bp-outfit-column" key={row.id}>
                          <header className="ps-bp-outfit-column-head">
                            <StylePreviewCategoryIcon row={row} />
                            <h3>{t(stylePreviewCategoryLabel(row))}</h3>
                          </header>

                          <article className={`ps-bp-outfit-primary${row.selected ? " ps-selected" : ""}`}>
                            <button
                              type="button"
                              className="ps-bp-fitting-select"
                              aria-label={row.selected ? t("Selected for try-on") : t("Select for try-on")}
                              aria-pressed={row.selected}
                              onClick={() => toggleStylePreviewRow(row.id)}
                            >
                              {row.selected ? <CheckIcon /> : null}
                            </button>
                            <span className="ps-bp-outfit-primary-image">
                              <StylePreviewGarmentImage src={row.displayImage || row.image} alt={row.title} apiKey={apiKey} apiUrl={apiUrl} eager />
                            </span>
                            <strong>{row.title}</strong>
                            <small>{row.recommendedSize ? `${t("Size")}: ${row.recommendedSize}` : row.garmentType}</small>
                          </article>

                          <div className="ps-bp-outfit-alternatives">
                            {options
                              .map((option, optionIndex) => ({ option, optionIndex }))
                              .filter(({ optionIndex }) => optionIndex !== (row.optionIndex ?? 0))
                              .slice(0, 4)
                              .map(({ option, optionIndex }) => (
                                <button
                                  key={`${row.id}-${option.productId}-${optionIndex}`}
                                  type="button"
                                  className="ps-bp-outfit-alternative"
                                  onClick={() => selectStylePreviewOption(row.id, optionIndex)}
                                  aria-label={`${t("Choose")} ${option.title}`}
                                >
                                  <span>
                                    <StylePreviewGarmentImage src={option.displayImage || option.image} alt={option.title} apiKey={apiKey} apiUrl={apiUrl} />
                                  </span>
                                  <span>
                                    <strong>{option.title}</strong>
                                    <small>{option.recommendedSize ? `${t("Size")}: ${option.recommendedSize}` : option.garmentType}</small>
                                  </span>
                                </button>
                              ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </section>

                <aside className="ps-bp-outfit-summary" aria-label={t("Selected look")}>
                  <div className="ps-bp-outfit-look-head">
                    <div className="ps-bp-outfit-look-title">
                      <strong>{t("Look")} {stylePreviewOutfitIndex + 1} {t("of")} {outfitCount}</strong>
                      <div className="ps-bp-outfit-carousel-controls" aria-label={t("Outfit carousel controls")}>
                        <button
                          type="button"
                          onClick={() => selectStylePreviewOutfit((stylePreviewOutfitIndex - 1 + outfitCount) % outfitCount)}
                          aria-label={t("Previous look")}
                          disabled={outfitCount <= 1}
                        >
                          <ArrowLeftIcon />
                        </button>
                        <button
                          type="button"
                          className="ps-next"
                          onClick={() => selectStylePreviewOutfit((stylePreviewOutfitIndex + 1) % outfitCount)}
                          aria-label={t("Next look")}
                          disabled={outfitCount <= 1}
                        >
                          <ArrowLeftIcon />
                        </button>
                      </div>
                    </div>
                    <div className="ps-bp-outfit-dots" aria-label={t("Outfit pagination")}>
                      {Array.from({ length: outfitCount }, (_, index) => (
                        <button
                          key={index}
                          type="button"
                          className={index === stylePreviewOutfitIndex ? "ps-active" : ""}
                          onClick={() => selectStylePreviewOutfit(index)}
                          aria-label={`${t("Look")} ${index + 1}`}
                          aria-current={index === stylePreviewOutfitIndex ? "true" : undefined}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="ps-bp-outfit-summary-body">
                    <h3>{selectedItemCount} {t("items in your look")}</h3>
                    <div className="ps-bp-outfit-summary-list">
                      <article>
                        <span className="ps-bp-outfit-summary-image">
                          <StylePreviewGarmentImage src={productImage} alt={productTitle} apiKey={apiKey} apiUrl={apiUrl} eager />
                        </span>
                        <span className="ps-bp-outfit-summary-copy">
                          <strong>{productTitle}</strong>
                          {currentRecommendedSize && <small>{t("Size")} {currentRecommendedSize}</small>}
                        </span>
                        <span className="ps-bp-outfit-summary-pinned"><SparkleIcon size={12} /> {t("Pinned")}</span>
                      </article>
                      {selectedStyleRows.map((row) => (
                        <article key={row.id}>
                          <span className="ps-bp-outfit-summary-image">
                            <StylePreviewGarmentImage src={row.displayImage || row.image} alt={row.title} apiKey={apiKey} apiUrl={apiUrl} />
                          </span>
                          <span className="ps-bp-outfit-summary-copy">
                            <strong>{row.title}</strong>
                            <small>{row.recommendedSize ? `${t("Size")} ${row.recommendedSize}` : row.garmentType}</small>
                          </span>
                        </article>
                      ))}
                    </div>
                  </div>

                  <div className="ps-bp-outfit-summary-footer">
                    <div className="ps-bp-outfit-total">
                      <strong>
                        {t("Total")} <small>({selectedItemCount} {t("items")})</small>
                      </strong>
                      <span>{selectedItemCount} {t("selected")}</span>
                    </div>
                    {error && <p className="ps-bpm-error">{error}</p>}
                    <button
                      type="button"
                      className="ps-bpm-next-btn ps-bp-style-submit ps-bp-fitting-cta"
                      onClick={handleNext}
                      disabled={selectedStyleRows.length === 0 || stylePreviewSubmitting}
                      aria-busy={stylePreviewSubmitting}
                    >
                      {stylePreviewSubmitting ? t("Starting try-on…") : t("Complete My Look")}
                    </button>
                    <small>{t("Your try-on starts after you confirm this outfit.")}</small>
                  </div>
                </aside>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ── Mobile AI scan — Step 2 of 2: body details (height / weight / bra) ──
  if (step === "scan-details" && isMobile) {
    return (
      <div className="ps-bp-wrapper">
        {/* Visual 2-step progress: Step 01 (photo) done, Step 02 (details) active. */}
        <div className="ps-bp-scan-progress">
          <button
            type="button"
            className="ps-bp-scan-progress-back"
            onClick={() => setStep("photo")}
            aria-label={t("Back")}
          >
            ←
          </button>
          <div className="ps-bp-scan-progress-track">
            <div className="ps-bp-scan-progress-step ps-done">
              <span className="ps-bp-scan-progress-dot">1</span>
              <span className="ps-bp-scan-progress-label">{t("Photo")}</span>
            </div>
            <div className="ps-bp-scan-progress-line ps-active" />
            <div className="ps-bp-scan-progress-step ps-active">
              <span className="ps-bp-scan-progress-dot">2</span>
              <span className="ps-bp-scan-progress-label">{t("Details")}</span>
            </div>
          </div>
          <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
        </div>

        <div className={`ps-bp-scan-details-center${isShoeReferenceMode ? " ps-shoe-scan-details" : ""}`}>
          {isShoeReferenceMode ? (
            <>
              {renderShoeReferenceFields()}
              {error && <p className="ps-bpm-error">{error}</p>}
            </>
          ) : (
            <>
              <h2 className="ps-bp-scan-details-title">{t("Your body details")}</h2>
              <p className="ps-bp-scan-details-sub">{t("These calibrate the AI — all required.")}</p>

              <div className="ps-bp-system-toggle">
                <button className={`ps-bp-system-btn${!isImperialMode ? " ps-bp-system-active" : ""}`} onClick={photoSwitchToMetric} type="button">{t("Metric")}</button>
                <button className={`ps-bp-system-btn${isImperialMode ? " ps-bp-system-active" : ""}`} onClick={photoSwitchToImperial} type="button">{t("Imperial")}</button>
              </div>

              <div className="ps-bp-inline-fields">
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
                  {hUnit === "in" ? (
                    <div className="ps-bp-inline-input-group">
                      <input type="number" className="ps-bp-inline-input" placeholder="5" value={heightFeet} onChange={e => setHeightFeet(e.target.value)} min={3} max={8} />
                      <span className="ps-bp-inline-unit">ft</span>
                      <input type="number" className="ps-bp-inline-input" placeholder="10" value={heightInches} onChange={e => setHeightInches(e.target.value)} min={0} max={11} />
                      <span className="ps-bp-inline-unit">in</span>
                    </div>
                  ) : (
                    <div className="ps-bp-inline-input-group">
                      <input type="number" className="ps-bp-inline-input" placeholder="173" value={height} onChange={e => setHeight(e.target.value)} min={100} max={250} />
                      <span className="ps-bp-inline-unit">cm</span>
                    </div>
                  )}
                </div>
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
                  <div className="ps-bp-inline-input-group">
                    <input type="number" className="ps-bp-inline-input" placeholder={wUnit === "kg" ? "75" : "165"} value={weight} onChange={e => setWeight(e.target.value)} min={wUnit === "kg" ? 30 : 66} max={wUnit === "kg" ? 300 : 660} />
                    <span className="ps-bp-inline-unit">{wUnit}</span>
                  </div>
                </div>

                {showGenderSpecificDetails && (
                  <div ref={inlineBraWrapRef} className="ps-bp-inline-bra-fields">
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("BRA REGION")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={braSizeRegion}
                          options={Object.keys(BAND_SIZES)}
                          isOpen={inlineBraOpen === "region"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "region" ? null : "region")}
                          onSelect={(v) => { setBraSizeRegion(v); setBandSize(null); setCupSize(null); setInlineBraOpen(null); }}
                          direction="up"
                        />
                      </div>
                    </div>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("BAND")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={bandSize}
                          options={BAND_SIZES[braSizeRegion] || BAND_SIZES.US}
                          placeholder="—"
                          isOpen={inlineBraOpen === "band"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "band" ? null : "band")}
                          onSelect={(v) => { setBandSize(v); setInlineBraOpen(null); }}
                          direction="up"
                        />
                      </div>
                    </div>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("CUP")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={cupSize}
                          options={CUP_SIZES[braSizeRegion] || CUP_SIZES.US}
                          placeholder="—"
                          direction="up"
                          isOpen={inlineBraOpen === "cup"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "cup" ? null : "cup")}
                          onSelect={(v) => { setCupSize(v); setInlineBraOpen(null); }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && <p className="ps-bpm-error">{error}</p>}
            </>
          )}
        </div>

        <div className="ps-bpm-bottom">
          <button
            type="button"
            className="ps-bpm-next-btn"
            onClick={handleNext}
            disabled={!isShoeReferenceMode && (stylePreviewAvailability === "loading" || stylePreviewLoading)}
          >
            {isShoeReferenceMode
              ? t(!shoeBrandId ? "Choose brand" : !shoeSizeValue ? "Choose size" : "Analyze My Size")
              : stylePreviewAvailability === "loading"
                ? t("Loading Outfit Builder…")
                : shouldOfferStylePreview
                  ? t("Build My Outfit")
                  : t("Analyze My Size")}
          </button>
          {(!simplePhotoOnly || showSimpleAccessoryMobileTabs) && (
            <MobileBottomTabs
              mode="scan"
              onSwitchToManual={() => setStep("basics")}
              onSwitchToScan={() => { /* already on scan */ }}
              t={t}
            />
          )}
        </div>
      </div>
    );
  }

	  if (step === "photo" || step === "scan-details") {
    // Photo-step gating. Height + weight are now entered IN THIS STEP
    // (not in the basics step any more) so the user has one focused
    // screen with everything the backend needs for accurate estimation.
    // (scan-details only matches here for the desktop fallback — the
    //  mobile branch above already returned for scan-details on phones.)
    const heightFilled = hUnit === "in"
      ? parseFloat(heightFeet) > 0 || parseFloat(heightInches) > 0
      : parseFloat(height) > 0;
    const weightFilled = parseFloat(weight) > 0;
    const photoFilled = !!photoPreview;
    const hasValidBasicsPhoto = heightFilled && weightFilled;

    // Dynamic "Analyze" button label — tells the user exactly what's
    // missing before sizing can run. Order mirrors the natural flow:
    // photo → height → weight → analyze.
    const analyzeMissing: string[] = [];
    if (photoProcessing) analyzeMissing.push(t("Analyzing photo…"));
    if (!photoFilled && !photoProcessing) analyzeMissing.push(t("Upload a photo"));
    if (isShoeReferenceMode) {
      if (!shoeBrandId) analyzeMissing.push(t("Choose your shoe brand"));
      if (shoeBrandId && !shoeSizeValue) analyzeMissing.push(t("Choose size"));
    } else {
      if (!heightFilled) analyzeMissing.push(t("Enter your height"));
      if (!weightFilled) analyzeMissing.push(t("Enter your weight"));
      if (!basicOnly && showGenderSpecificDetails && (!bandSize || !cupSize)) analyzeMissing.push(t("Enter your bra size"));
    }
	    const stylePreviewAvailabilityLoading = stylePreviewAvailability === "loading";
	    const analyzeDisabled = analyzeMissing.length > 0 || stylePreviewAvailabilityLoading || stylePreviewLoading;
	    const readyAnalyzeLabel = stylePreviewAvailabilityLoading
	      ? t("Loading Outfit Builder…")
	      : shouldOfferStylePreview
	        ? t("Build My Outfit")
	        : t("Analyze My Size");
	    const analyzeLabel = analyzeDisabled ? (analyzeMissing[0] ?? readyAnalyzeLabel) : readyAnalyzeLabel;
	    const compactHeightWeightAccessory = simplePhotoOnly && simpleAccessoryLayout && !isShoeReferenceMode;
	    const maleStandardPhotoDetails = !isWomen && !simplePhotoOnly && !isShoeReferenceMode;

    // Mobile — Step 1 of 2 of the AI scan flow (photo upload only).
    // Step 2 (height/weight/bra) lives in the scan-details branch above.
    if (isMobile) {
      return (
        <div className="ps-bp-wrapper">
          {/* Visual 2-step progress: Step 01 (photo) active, Step 02 pending. */}
          <div className="ps-bp-scan-progress">
            <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
            <div className="ps-bp-scan-progress-track">
              <div className="ps-bp-scan-progress-step ps-active">
                <span className="ps-bp-scan-progress-dot">1</span>
                <span className="ps-bp-scan-progress-label">{t("Photo")}</span>
              </div>
              <div className="ps-bp-scan-progress-line" />
              <div className="ps-bp-scan-progress-step">
                <span className="ps-bp-scan-progress-dot">2</span>
                <span className="ps-bp-scan-progress-label">{t("Details")}</span>
              </div>
            </div>
            <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
          </div>

          <PhotoStepMobile
            photoPreview={photoPreview}
            handlePhotoSelect={handlePhotoSelect}
            handleRemovePhoto={handleRemovePhoto}
            onAnalyze={handleNext}
            onSwitchToManual={() => { if (!simplePhotoOnly) setStep("basics"); }}
            error={error}
            photoStepHeight={photoStepHeight}
            onPhotoStepHeightChange={setPhotoStepHeight}
            ageConfirmed={ageConfirmed}
            onAgeConfirmedChange={(v) => { setAgeConfirmed(v); if (v === true) setError(""); }}
            primaryLabel={isShoeReferenceMode ? t("Choose size") : t("Continue")}
            compactAgeGate
            photoProcessing={photoProcessing}
            photoStatus={photoStatus}
            photoRejection={photoRejection}
            onClearRejection={() => setPhotoRejection(null)}
            photoHelpSlot={(
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPhotoHelpOpen(v => !v); }}
                aria-label={t("How to take a good photo")}
                className={`ps-bp-photo-help-chip${photoHelpOpen ? " ps-active" : ""}`}
              >
                <span className="ps-bp-photo-help-chip-mark" aria-hidden="true">?</span>
                <span>{t("How to take a good photo")}</span>
              </button>
            )}
            hideTabs={simplePhotoOnly && !showSimpleAccessoryMobileTabs}
            t={t}
          />

          {/* Bottom drawer with Do/Don't/Tip — toggled by the "?" pill above. */}
          {!photoPreview && photoHelpOpen && (
            <>
              <div className="ps-bp-photo-help-backdrop" onClick={() => setPhotoHelpOpen(false)} aria-hidden="true" />
              <div role="dialog" aria-label={t("How to take the best photo")} className="ps-bp-photo-help">
                <div className="ps-bp-photo-help-handle" aria-hidden="true" />
                <div className="ps-bp-photo-help-header">
                  <span className="ps-bp-photo-help-title">{t("How to take the best photo")}</span>
                  <button type="button" onClick={() => setPhotoHelpOpen(false)} aria-label={t("Close")} className="ps-bp-photo-help-close">×</button>
                </div>
                <div className="ps-bp-photo-help-section ps-bp-photo-help-do">
                  <div className="ps-bp-photo-help-section-label">✓ {t("Do")}</div>
                  <div className="ps-bp-photo-help-section-body">
                    {t("Stand facing the camera with your full body in frame")}<br />
                    {t("Hold the phone at WAIST height (not eye level)")}<br />
                    {t("Stand with your HEELS against a wall for best accuracy")}<br />
                    {t("Use natural or even lighting (e.g. near a window)")}<br />
                    {t("Wear fitted, simple clothing (e.g. neutral colors)")}<br />
                    {t("Choose a plain background (a light wall is ideal)")}<br />
                    {t("Stand straight and still, arms relaxed by your sides")}
                  </div>
                </div>
                <div className="ps-bp-photo-help-section ps-bp-photo-help-dont">
                  <div className="ps-bp-photo-help-section-label">✗ {t("Don't")}</div>
                  <div className="ps-bp-photo-help-section-body">
                    {t("Don't wear loose, baggy, or layered clothing")}<br />
                    {t("Don't sit, pose, or bend your body")}<br />
                    {t("Don't use strong backlighting")}<br />
                    {t("Don't take mirror photos or selfies")}<br />
                    {t("Don't apply filters, effects, or edits")}
                  </div>
                </div>
                <div className="ps-bp-photo-help-section ps-bp-photo-help-tip">
                  <strong>💡 {t("Quick Tip")}:</strong> {t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }
    return (
      <div className="ps-bp-wrapper">
        <div style={{ display: "flex", gap: "1.2vw", padding: "1.5vw", width: "100%", flex: "1 1 auto", minHeight: 0, alignItems: "stretch", position: "relative", overflow: "hidden" }}>

          {/* Left: Upload area + Legal Notice (after age confirmed) */}
          <div style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", gap: "0.7vw" }}>
            <div
              onClick={() => { if (!photoPreview && ageConfirmed === true) fileInputRef.current?.click(); }}
              onMouseEnter={() => { if (!photoPreview && ageConfirmed === true) setUploadHover(true); }}
              onMouseLeave={() => setUploadHover(false)}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (photoPreview || ageConfirmed !== true) return;
                const file = e.dataTransfer?.files?.[0];
                if (!file || !fileInputRef.current) return;
                // Pipe the dropped file through the existing handlePhotoSelect
                // (age check + compress + preview) by simulating a change event
                // on the hidden input. DataTransfer is the only way to assign
                // FileList programmatically.
                const dt = new DataTransfer();
                dt.items.add(file);
                fileInputRef.current.files = dt.files;
                fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
              }}
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                // Border: accent solid when a photo is loaded; accent dashed
                // on hover once age is confirmed (invites clicking); grey
                // dashed otherwise. Gives the "this is now interactive"
                // cue the moment the user clears the age gate.
                border: photoPreview
                  ? "2px solid var(--ps-accent)"
                  : (ageConfirmed === true && uploadHover)
                    ? "2px dashed var(--ps-accent)"
                    : "2px dashed var(--ps-border-color)",
                borderRadius: "0.5vw",
                cursor: photoPreview ? "default" : (ageConfirmed === true ? "pointer" : "default"),
                position: "relative",
                // Very subtle tint when the age-gate is unanswered — tells
                // the user "this section is waiting on something" without
                // obscuring the content. Hover-accent tint replaces it the
                // moment the gate is cleared.
                background: photoPreview
                  ? "var(--ps-bg-secondary)"
                  : (ageConfirmed === true && uploadHover)
                    ? "rgba(33,84,239,0.04)"
                    : ageConfirmed === null
                      ? "rgba(0,0,0,0.025)"
                      : "var(--ps-bg-secondary)",
                transition: "border-color 0.18s, background 0.18s", overflow: "hidden",
              }}
            >
              {!photoPreview && !compactHeightWeightAccessory && !maleStandardPhotoDetails && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPhotoHelpOpen(v => !v); }}
                  aria-label={t("How to take a good photo")}
                  title={t("How to take a good photo")}
                  className={`ps-bp-photo-help-chip${photoHelpOpen ? " ps-active" : ""}`}
                >
                  <span className="ps-bp-photo-help-chip-mark" aria-hidden="true">?</span>
                  <span>{t("How to take a good photo")}</span>
                </button>
              )}

              {!photoPreview && !compactHeightWeightAccessory && !maleStandardPhotoDetails && photoHelpOpen && (
                <div
                  role="dialog"
                  aria-label={t("How to take the best photo")}
                  className="ps-bp-photo-help ps-bp-photo-help-upload"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="ps-bp-photo-help-handle" aria-hidden="true" />
                  <div className="ps-bp-photo-help-header">
                    <span className="ps-bp-photo-help-title">{t("How to take the best photo")}</span>
                    <button
                      type="button"
                      onClick={() => setPhotoHelpOpen(false)}
                      aria-label={t("Close")}
                      className="ps-bp-photo-help-close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="ps-bp-photo-help-section ps-bp-photo-help-do">
                    <div className="ps-bp-photo-help-section-label">✓ {t("Do")}</div>
                    <div className="ps-bp-photo-help-section-body">
                      {t("Stand facing the camera with your full body in frame")}<br />
                      {t("Hold the phone at WAIST height (not eye level)")}<br />
                      {t("Stand with your HEELS against a wall for best accuracy")}<br />
                      {t("Use natural or even lighting (e.g. near a window)")}<br />
                      {t("Wear fitted, simple clothing (e.g. neutral colors)")}<br />
                      {t("Choose a plain background (a light wall is ideal)")}<br />
                      {t("Stand straight and still, arms relaxed by your sides")}
                    </div>
                  </div>

                  <div className="ps-bp-photo-help-section ps-bp-photo-help-dont">
                    <div className="ps-bp-photo-help-section-label">✗ {t("Don't")}</div>
                    <div className="ps-bp-photo-help-section-body">
                      {t("Don't wear loose, baggy, or layered clothing")}<br />
                      {t("Don't sit, pose, or bend your body")}<br />
                      {t("Don't use strong backlighting")}<br />
                      {t("Don't take mirror photos or selfies")}<br />
                      {t("Don't apply filters, effects, or edits")}
                    </div>
                  </div>

                  <div className="ps-bp-photo-help-section ps-bp-photo-help-tip">
                    <strong>💡 {t("Quick Tip")}:</strong> {t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
                  </div>
                </div>
              )}

              {/* Analyzing overlay — covers the upload zone while we run the
                  age check + compress. User sees "Analyzing photo…" + a spinner
                  instead of a stale preview. */}
              {photoProcessing && (
                <div className="ps-bp-photo-overlay" onClick={e => e.stopPropagation()}>
                  <div className="ps-bp-photo-overlay-spinner" aria-hidden="true" />
                  <span className="ps-bp-photo-overlay-label">{photoStatus || t("Analyzing photo…")}</span>
                </div>
              )}
              {photoPreview ? (
                <>
                  <img src={photoPreview} alt={t("preview")} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  <button onClick={e => { e.stopPropagation(); handleRemovePhoto(); }} style={{ position: "absolute", top: "0.5vw", right: "0.5vw", width: "1.4vw", height: "1.4vw", borderRadius: "50%", background: "rgba(0,0,0,0.6)", border: "none", color: "#fff", fontSize: "0.7vw", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                </>
              ) : (
                <>
                  <UploadIcon size={32} />
                  <span style={{ fontSize: "0.85vw", fontWeight: 600, color: "var(--ps-text-primary)", marginTop: "0.5vw" }}>{t("Upload your photo")}</span>
                  <span style={{ fontSize: "0.6vw", color: "var(--ps-text-muted)", marginTop: "0.2vw" }}>{t("Click or drag a full-body photo")}</span>
                  <span style={{ fontSize: "0.5vw", color: "var(--ps-text-dim)", marginTop: "0.4vw" }}>{t("JPEG, PNG, WebP, AVIF (max 10MB)")}</span>

                  {/* Photo-rejection card — shown when age-check failed.
                      Sits in the same slot as the "Are you 18 or older?" chip
                      so the rejection feedback feels native to that flow.
                      Always wins over the age-confirm chip when present. */}
                  {photoRejection && (
                    <div
                      role="alert"
                      onClick={e => e.stopPropagation()}
                      style={{
                        marginTop: "1.1vw",
                        padding: "0.75vw 1.1vw",
                        background: "#FFFFFF",
                        border: "1px solid rgba(220, 38, 38, 0.35)",
                        borderRadius: "0.55vw",
                        boxShadow: "0 6px 18px -8px rgba(220, 38, 38, 0.25)",
                        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.55vw",
                        cursor: "default", maxWidth: "20vw",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.45vw" }}>
                        <svg viewBox="0 0 24 24" width="0.85vw" height="0.85vw" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span style={{ color: "#dc2626", fontSize: "0.7vw", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{t("Different photo needed")}</span>
                      </div>
                      <div style={{ fontSize: "0.62vw", color: "var(--ps-text-secondary)", lineHeight: 1.55 }}>
                        {photoRejection}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPhotoRejection(null); fileInputRef.current?.click(); }}
                        style={{
                          padding: "0.35vw 0.8vw",
                          background: "var(--ps-accent)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "0.35vw",
                          fontSize: "0.62vw",
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >{t("Try another photo")}</button>
                    </div>
                  )}

                  {/* Inline age-verification chip — sits a breath below
                      the JPEG/PNG hint, not floating in the middle of
                      the zone. Only shown when the user hasn't answered
                      yet (ageConfirmed === null). Clicking either button
                      doesn't trigger the zone's onClick because the
                      ageConfirmed === true guard filters it out. */}
                  {ageConfirmed === null && !photoRejection && (
                    <div
                      role="dialog"
                      aria-labelledby="ps-bp-age-q"
                      onClick={e => e.stopPropagation()}
                      style={{
                        marginTop: "1.1vw",
                        padding: "0.75vw 1.1vw",
                        background: "#FFFFFF",
                        border: "1px solid var(--ps-border-color)",
                        borderRadius: "0.55vw",
                        boxShadow: "0 6px 18px -8px rgba(17,24,39,0.22)",
                        display: "flex", alignItems: "center", gap: "0.8vw",
                        cursor: "default",
                        animation: "ps-cpw-age-gate-in 0.28s ease-out both",
                      }}
                    >
                      <span id="ps-bp-age-q" style={{ fontSize: "0.78vw", fontWeight: 600, color: "var(--ps-text-primary)" }}>
                        {t("Are you 18 or older?")}
                      </span>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setAgeConfirmed(true); setError(""); }}
                        style={{
                          padding: "0.4vw 1vw", borderRadius: "999px",
                          background: "var(--ps-accent)", color: "#FFFFFF",
                          border: "1px solid var(--ps-accent)",
                          fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {t("Yes")}
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setAgeConfirmed(false); }}
                        style={{
                          padding: "0.4vw 1vw", borderRadius: "999px",
                          background: "transparent", color: "var(--ps-text-secondary)",
                          border: "1px solid var(--ps-border-color)",
                          fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {t("No")}
                      </button>
                    </div>
                  )}

                  {ageConfirmed === false && (
                    <div
                      role="alert"
                      onClick={e => e.stopPropagation()}
                      style={{
                        marginTop: "1.1vw",
                        padding: "0.75vw 1.1vw",
                        background: "#FFFFFF",
                        border: "1px solid rgba(192,38,38,0.4)",
                        borderRadius: "0.55vw",
                        boxShadow: "0 6px 18px -8px rgba(192,38,38,0.22)",
                        display: "flex", alignItems: "center", gap: "0.8vw",
                        cursor: "default",
                      }}
                    >
                      <span style={{ fontSize: "0.78vw", fontWeight: 600, color: "#C02626" }}>
                        {t("Under 18 — can't process this photo.")}
                      </span>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); setAgeConfirmed(null); }}
                        style={{
                          padding: "0.4vw 1vw", borderRadius: "999px",
                          background: "transparent", color: "var(--ps-text-secondary)",
                          border: "1px solid var(--ps-border-color)",
                          fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700, cursor: "pointer",
                        }}
                      >
                        {t("Go back")}
                      </button>
                    </div>
                  )}

                  {/* Legal Notice — small print pinned to the bottom INSIDE
                      the dashed upload box, only after the user confirms 18+.
                      Sits as a footnote so it shares space with the upload CTA
                      without taking a separate card slot in the column. */}
                  {ageConfirmed === true && (
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        position: "absolute", left: "0.8vw", right: "0.8vw", bottom: "0.6vw",
                        fontSize: "0.62vw", lineHeight: 1.5,
                        color: "var(--ps-text-secondary)",
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3vw", verticalAlign: "middle" }}>
                        <svg viewBox="0 0 24 24" width="0.8vw" height="0.8vw" fill="none" stroke="#2154ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span style={{ fontWeight: 700, color: "#2154ef", letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("Legal Notice")}</span>
                      </span>
                      {" — "}
                      {t("Your image will be used to generate a virtual try-on preview showing how selected items may look and fit. Images are processed securely and are not stored after generation.")}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Soft vertical divider between upload and body-details columns.
              Gradient fade at the top and bottom so it feels like a breath
              of a line rather than a hard full-height rule. */}
          <div aria-hidden="true" style={{
            width: "1px",
            alignSelf: "stretch",
            background: "linear-gradient(to bottom, transparent 0%, var(--ps-border-subtle) 18%, var(--ps-border-subtle) 82%, transparent 100%)",
            flexShrink: 0,
          }} />

          {/* Right: Height + Weight (and bra inputs for women). Do/Don't +
              Quick Tip are hidden behind a "?" icon popover to reduce
              visual clutter. Height and weight are mandatory — the Analyze
              button stays disabled with a helpful label ("Upload a photo",
              "Enter your height", etc.) until all three inputs are supplied.

              Layout: header pinned to top, then a flex-1 spacer wrapper
              centers the toggle + inputs vertically — keeps the men variant
              (just height/weight) from looking lost in a tall column, and
              women's bra inputs still flow naturally below. */}
	          <div style={{
	            flex: compactHeightWeightAccessory ? "0 1 min(34vw, 360px)" : 1,
	            display: "flex",
	            flexDirection: "column",
	            justifyContent: "flex-start",
	            position: "relative",
	            gap: compactHeightWeightAccessory ? "0.8vw" : "0.8vw",
	            minWidth: compactHeightWeightAccessory ? "280px" : undefined,
	          }}>
            {isShoeReferenceMode ? (
              <div className="ps-shoe-ref-desktop">
                {renderShoeReferenceFields()}
              </div>
            ) : (
              <>

	            <div>
	              <div className={`ps-bp-photo-details-head${compactHeightWeightAccessory || maleStandardPhotoDetails ? " ps-bp-photo-details-head-simple" : ""}`}>
	                <h3>{t("Body details")}</h3>
	                {compactHeightWeightAccessory || maleStandardPhotoDetails ? (
	                  <button
	                    type="button"
	                    onClick={(e) => { e.stopPropagation(); setPhotoHelpOpen(v => !v); }}
	                    aria-label={t("How to take a good photo")}
	                    title={t("How to take a good photo")}
	                    className={`ps-bp-photo-help-chip${photoHelpOpen ? " ps-active" : ""}`}
	                    style={{ position: "static", maxWidth: "none", flexShrink: 0 }}
	                  >
	                    <span className="ps-bp-photo-help-chip-mark" aria-hidden="true">?</span>
	                    <span>{t("How to take a good photo")}</span>
	                  </button>
	                ) : (
	                  <div className="ps-bp-system-toggle ps-bp-system-toggle-compact">
	                    <button className={`ps-bp-system-btn${!isImperialMode ? " ps-bp-system-active" : ""}`} onClick={photoSwitchToMetric} type="button">{t("Metric")}</button>
	                    <button className={`ps-bp-system-btn${isImperialMode ? " ps-bp-system-active" : ""}`} onClick={photoSwitchToImperial} type="button">{t("Imperial")}</button>
	                  </div>
	                )}
	              </div>
	              {(compactHeightWeightAccessory || maleStandardPhotoDetails) && (
	                <p style={{ margin: "0.2vw 0 0", fontSize: "0.65vw", color: "var(--ps-text-muted)" }}>
	                  {t("These calibrate the AI — all required.")}
	                </p>
	              )}
	            </div>

	            {(compactHeightWeightAccessory || maleStandardPhotoDetails) && (
	              <div className={`ps-bp-system-toggle ps-bp-system-toggle-compact${maleStandardPhotoDetails ? " ps-bp-system-toggle-large" : ""}`} style={{ alignSelf: "center", marginTop: "1.2vw" }}>
	                <button className={`ps-bp-system-btn${!isImperialMode ? " ps-bp-system-active" : ""}`} onClick={photoSwitchToMetric} type="button">{t("Metric")}</button>
	                <button className={`ps-bp-system-btn${isImperialMode ? " ps-bp-system-active" : ""}`} onClick={photoSwitchToImperial} type="button">{t("Imperial")}</button>
	              </div>
	            )}

	            {(compactHeightWeightAccessory || maleStandardPhotoDetails) && photoHelpOpen && (
	              <div
	                role="dialog"
	                aria-label={t("How to take the best photo")}
	                style={{
	                  position: "absolute", top: "2.2vw", right: 0,
	                  width: "min(360px, 22vw)",
	                  background: "#FFFFFF",
	                  border: "1px solid var(--ps-border-subtle)",
	                  borderRadius: "0.8vw",
	                  boxShadow: "0 20px 40px -12px rgba(17,24,39,0.25), 0 8px 16px -8px rgba(17,24,39,0.15)",
	                  padding: "0.8vw 1vw",
	                  display: "flex", flexDirection: "column", gap: "0.6vw",
	                  maxHeight: "min(70dvh, 520px)",
	                  overflowY: "auto",
	                  zIndex: 5,
	                }}
	              >
	                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
	                  <span style={{ fontSize: "0.8vw", fontWeight: 700, color: "var(--ps-text-primary)" }}>{t("How to take the best photo")}</span>
	                  <button
	                    type="button"
	                    onClick={() => setPhotoHelpOpen(false)}
	                    aria-label={t("Close")}
	                    style={{ width: "1.4vw", height: "1.4vw", borderRadius: "50%", background: "transparent", border: "none", color: "var(--ps-text-muted)", cursor: "pointer", fontSize: "1vw", lineHeight: 1 }}
	                  >
	                    ×
	                  </button>
	                </div>
	                <div style={{ background: "#ddfbe7", borderRadius: "0.5vw", padding: "0.55vw 0.75vw" }}>
	                  <div style={{ color: "#1c9d4c", fontSize: "0.7vw", fontWeight: 700, marginBottom: "0.3vw" }}>✓ {t("Do")}</div>
	                  <div style={{ fontSize: "0.6vw", color: "var(--ps-text-primary)", lineHeight: 1.6 }}>
	                    {t("Stand facing the camera with your full body in frame")}<br />
	                    {t("Use natural or even lighting")}<br />
	                    {t("Wear fitted, simple clothing")}<br />
	                    {t("Choose a plain background")}
	                  </div>
	                </div>
	                <div style={{ background: "#fee2e2", borderRadius: "0.5vw", padding: "0.55vw 0.75vw" }}>
	                  <div style={{ color: "#dc2626", fontSize: "0.7vw", fontWeight: 700, marginBottom: "0.3vw" }}>✕ {t("Don't")}</div>
	                  <div style={{ fontSize: "0.6vw", color: "var(--ps-text-primary)", lineHeight: 1.6 }}>
	                    {t("Don't wear loose, baggy, or layered clothing")}<br />
	                    {t("Don't sit, pose, or bend your body")}<br />
	                    {t("Don't use strong backlighting")}
	                  </div>
	                </div>
	                <div style={{ background: "#dbeafe", borderRadius: "0.5vw", padding: "0.55vw 0.75vw", fontSize: "0.58vw", color: "var(--ps-text-primary)", lineHeight: 1.5 }}>
	                  <strong>{t("Quick Tip")}:</strong> {t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
	                </div>
	              </div>
	            )}

	            <div style={{ marginTop: compactHeightWeightAccessory || maleStandardPhotoDetails ? "auto" : "0.15vw", marginBottom: compactHeightWeightAccessory || maleStandardPhotoDetails ? "auto" : 0, minHeight: 0 }}>
              {/* "Loaded from profile" pill removed — was duplicating the
                   "Using X · start fresh" hint below for the same state. */}
              {activeProfileName && (
                <div className="ps-bp-profile-card ps-bp-profile-card-compact">
                  <div className="ps-bp-profile-card-copy">
                    <span className="ps-bp-profile-card-eyebrow">{t("Active Profile")}</span>
                    <strong>{activeProfileName}</strong>
                    <span>{t("Using saved measurements for this fit.")}</span>
                  </div>
                  {onStartFresh && (
                    <button type="button" className="ps-bp-profile-card-action" onClick={handleClearFromProfile}>
                      {t("Start Fresh")}
                    </button>
                  )}
                </div>
              )}
              {/* Height + Weight input rows — reuse the basics-step CSS classes
                  so the visual treatment is identical. */}
              <div className="ps-bp-inline-fields">
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
                  {hUnit === "in" ? (
                    <div className="ps-bp-inline-input-group">
                      <input type="number" className="ps-bp-inline-input" placeholder="5" value={heightFeet} onChange={e => setHeightFeet(e.target.value)} min={3} max={8} />
                      <span className="ps-bp-inline-unit">ft</span>
                      <input type="number" className="ps-bp-inline-input" placeholder="10" value={heightInches} onChange={e => setHeightInches(e.target.value)} min={0} max={11} />
                      <span className="ps-bp-inline-unit">in</span>
                    </div>
                  ) : (
                    <div className="ps-bp-inline-input-group">
                      <input type="number" className="ps-bp-inline-input" placeholder="173" value={height} onChange={e => setHeight(e.target.value)} min={100} max={250} />
                      <span className="ps-bp-inline-unit">cm</span>
                    </div>
                  )}
                </div>
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
                  <div className="ps-bp-inline-input-group">
                    <input type="number" className="ps-bp-inline-input" placeholder={wUnit === "kg" ? "75" : "165"} value={weight} onChange={e => setWeight(e.target.value)} min={wUnit === "kg" ? 30 : 66} max={wUnit === "kg" ? 300 : 660} />
                    <span className="ps-bp-inline-unit">{wUnit}</span>
                  </div>
                </div>

                {/* Women: bra size inputs (region + band + cup). Required so the
                    backend can use it to compute bust deterministically instead
                    of guessing from BMI/shoulder ratio. Custom mini-select drawer
                    UI (animated slide-fade) instead of native dropdowns. */}
                {showGenderSpecificDetails && (
                  <div ref={inlineBraWrapRef} className="ps-bp-inline-bra-fields">
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("BRA REGION")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={braSizeRegion}
                          options={Object.keys(BAND_SIZES)}
                          isOpen={inlineBraOpen === "region"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "region" ? null : "region")}
                          onSelect={(v) => { setBraSizeRegion(v); setBandSize(null); setCupSize(null); setInlineBraOpen(null); }}
                          direction="up"
                        />
                      </div>
                    </div>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("BAND")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={bandSize}
                          options={BAND_SIZES[braSizeRegion] || BAND_SIZES.US}
                          placeholder="—"
                          isOpen={inlineBraOpen === "band"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "band" ? null : "band")}
                          onSelect={(v) => { setBandSize(v); setInlineBraOpen(null); }}
                          direction="up"
                        />
                      </div>
                    </div>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("CUP")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={cupSize}
                          options={CUP_SIZES[braSizeRegion] || CUP_SIZES.US}
                          placeholder="—"
                          direction="up"
                          isOpen={inlineBraOpen === "cup"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "cup" ? null : "cup")}
                          onSelect={(v) => { setCupSize(v); setInlineBraOpen(null); }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Legal Notice was here — moved to under the upload zone (left column)
                so it sits next to the photo it talks about, only after the user
                confirms 18+. */}
              </>
            )}
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />


        {/* Mobile bottom action: Analyze button + MANUAL FIT/AI SCAN tabs */}
        {isMobile ? (
          <>
            <div className="ps-bpm-spacer" />
            <div className="ps-bpm-bottom">
              <button
                type="button"
                className="ps-bpm-next-btn"
                onClick={handleNext}
                disabled={!photoPreview || stylePreviewAvailability === "loading" || stylePreviewLoading}
              >
                {stylePreviewAvailability === "loading"
                  ? t("Loading Outfit Builder…")
                  : shouldOfferStylePreview
                    ? t("Build My Outfit")
                    : t("Analyze My Size")}
              </button>
              {(!simplePhotoOnly || showSimpleAccessoryMobileTabs) && (
                <MobileBottomTabs
                  mode="scan"
                  onSwitchToManual={() => setStep("basics")}
                  onSwitchToScan={() => { /* already on scan */ }}
                  t={t}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {/* Desktop — small inline escape hatch so the user can drop
                out of the AI photo flow and type their measurements
                directly. Mobile gets the same toggle via MobileBottomTabs. */}
            {!simplePhotoOnly && (
              <div className="ps-bp-photo-manual-switch">
                {t("Prefer not to upload a photo?")}{" "}
                <button
                  type="button"
                  className="ps-bp-photo-manual-link"
                  onClick={() => setStep("basics")}
                >
                  {t("Enter measurements manually")}
                </button>
              </div>
            )}
            <div className="ps-bp-nav">
              <button className="ps-bp-back-btn" onClick={handleBackStep} type="button">
                <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
              </button>
              <button
                className={`ps-bp-next-btn${analyzeDisabled ? " ps-bp-btn-disabled" : ""}`}
                onClick={handleNext}
                disabled={analyzeDisabled}
                type="button"
                title={analyzeDisabled ? analyzeMissing.join(" · ") : undefined}
              >
                {analyzeLabel}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="ps-bp-wrapper">
    <div className="ps-bp-layout">
      {/* Left panel: product image */}
      {(
        <div className="ps-bp-image">
          <img src={productImage} alt={productTitle} className="ps-bp-image-img" />
        </div>
      )}

      {/* Hidden file input — always rendered so photo step can access it */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoSelect}
        style={{ display: "none" }}
      />

      {/* Right: form panel */}
      <div className="ps-bp-root">
      {/* Step dots removed */}

      {/* ── Step: Basics ── */}
      {step === "basics" && (() => {
        // Unit toggle handlers — convert current values so the position is
        // preserved across the metric ↔ imperial switch.
        const switchToMetric = () => {
          if (hUnit === "in") {
            const totalIn = (parseFloat(heightFeet) || 5) * 12 + (parseFloat(heightInches) || 8);
            setHeight(String(Math.round(totalIn * 2.54)));
          }
          if (wUnit === "lbs" && weight) {
            setWeight(String(Math.round(parseFloat(weight) / 2.205)));
          }
          setHUnit("cm"); setWUnit("kg");
          setHeightUnit("cm"); setWeightUnit("kg"); setSizingUnit?.("cm");
        };
        const switchToImperial = () => {
          if (hUnit === "cm") {
            const cmVal = parseFloat(height) || 173;
            const totalIn = Math.round(cmVal / 2.54);
            setHeightFeet(String(Math.floor(totalIn / 12)));
            setHeightInches(String(totalIn % 12));
          }
          if (wUnit === "kg" && weight) {
            setWeight(String(Math.round(parseFloat(weight) * 2.205)));
          }
          setHUnit("in"); setWUnit("lbs");
          setHeightUnit("in"); setWeightUnit("lbs"); setSizingUnit?.("in");
        };

        // Mobile gets the dedicated reference-matching layout
        if (isMobile) {
          return (
            <BasicsStepMobile
              key="step-basics-mobile"
              hUnit={hUnit}
              wUnit={wUnit}
              isImperialMode={isImperialMode}
              height={height} setHeight={setHeight}
              heightFeet={heightFeet} setHeightFeet={setHeightFeet}
              heightInches={heightInches} setHeightInches={setHeightInches}
              weight={weight} setWeight={setWeight}
              age={age} setAge={setAge}
              switchToMetric={switchToMetric}
              switchToImperial={switchToImperial}
              // When an active profile is selected, neutralize the photo
              // entry points entirely. We don't want a tap on the upload
              // link or the bottom Scan tab to silently switch to the photo
              // step — those are alternative AI-sizing flows that don't
              // make sense once the user has a stored profile.
              onUploadPhoto={() => setStep("photo")}
              onSwitchToScan={() => setStep("photo")}
              hidePhotoOptions={basicOnly || (simplePhotoOnly && !showSimpleAccessoryMobileTabs) || hasActiveProfileWithMeasurements}
              showAge={!basicOnly && !simplePhotoOnly}
              onNext={handleNext}
              canProceed={!hasActiveProfileWithMeasurements || stylePreviewAvailability !== "loading"}
              fastPathLabel={hasActiveProfileWithMeasurements
                ? stylePreviewAvailability === "loading"
                  ? t("Loading Outfit Builder…")
                  : shouldOfferStylePreview
                    ? t("Build My Outfit")
                    : `${t("See Your Fit")}${getUnitLabel(hUnit) ? ` (${getUnitLabel(hUnit)})` : ""}`
                : undefined}
              activeProfileName={activeProfileName ?? null}
              onStartFresh={onStartFresh}
              error={error}
              t={t}
            />
          );
        }

        // Desktop layout — original number inputs (unchanged from pre-slider behavior)
        return (
        <div className="ps-bp-step ps-bp-step-enter" key="step-basics">
          <h2 className="ps-bp-title">{basicOnly || simplePhotoOnly ? t("Your Details") : t("Body Measurements")}</h2>
          {activeProfileName && (
            <div className="ps-bp-profile-card">
              <div className="ps-bp-profile-card-copy">
                <span className="ps-bp-profile-card-eyebrow">{t("Active Profile")}</span>
                <strong>{activeProfileName}</strong>
                <span>{t("Using saved measurements for this fit.")}</span>
              </div>
              {onStartFresh && (
                <button type="button" className="ps-bp-profile-card-action" onClick={handleClearFromProfile}>
                  {t("Start Fresh")}
                </button>
              )}
            </div>
          )}

          <div className="ps-bp-system-toggle">
            <button className={`ps-bp-system-btn${!isImperialMode ? " ps-bp-system-active" : ""}`} onClick={switchToMetric} type="button">{t("Metric")}</button>
            <button className={`ps-bp-system-btn${isImperialMode ? " ps-bp-system-active" : ""}`} onClick={switchToImperial} type="button">{t("Imperial")}</button>
          </div>

          <div className="ps-bp-inline-fields">
            <div className="ps-bp-inline-row">
              <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
              {hUnit === "in" ? (
                <div className="ps-bp-inline-input-group">
                  <input type="number" className="ps-bp-inline-input" placeholder="5" value={heightFeet} onChange={e => setHeightFeet(e.target.value)} min={3} max={8} />
                  <span className="ps-bp-inline-unit">ft</span>
                  <input type="number" className="ps-bp-inline-input" placeholder="10" value={heightInches} onChange={e => setHeightInches(e.target.value)} min={0} max={11} />
                  <span className="ps-bp-inline-unit">in</span>
                </div>
              ) : (
                <div className="ps-bp-inline-input-group">
                  <input type="number" className="ps-bp-inline-input" placeholder="173" value={height} onChange={e => setHeight(e.target.value)} min={100} max={250} />
                  <span className="ps-bp-inline-unit">cm</span>
                </div>
              )}
            </div>
            <div className="ps-bp-inline-row">
              <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
              <div className="ps-bp-inline-input-group">
                <input type="number" className="ps-bp-inline-input" placeholder={wUnit === "kg" ? "75" : "165"} value={weight} onChange={e => setWeight(e.target.value)} min={wUnit === "kg" ? 30 : 66} max={wUnit === "kg" ? 300 : 660} />
                <span className="ps-bp-inline-unit">{wUnit}</span>
              </div>
            </div>
            {!basicOnly && !simplePhotoOnly && (
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("AGE")}</span>
                <div className="ps-bp-inline-input-group">
                  <input type="number" className="ps-bp-inline-input" placeholder="30" value={age} onChange={e => setAge(e.target.value)} min={13} max={100} />
                  <span className="ps-bp-inline-unit">{t("years")}</span>
                </div>
              </div>
            )}
          </div>

          {/* Photo-upload CTA. The photo step has its own basics validation
              (validateBasics fires on submit there too), so we don't gate
              the navigation here — the user can click through, fill what's
              missing on the photo step, and submit. */}
          {!basicOnly && !simplePhotoOnly && (
            <button
              className="ps-bp-photo-cta"
              onClick={() => setStep("photo")}
              type="button"
            >
              <CameraUploadIcon />
              <span>{t("Or upload a photo for AI sizing")}</span>
              <span className="ps-bp-photo-cta-arrow">&rarr;</span>
            </button>
          )}

          {error && <p className="ps-bp-error">{error}</p>}
        </div>
        );
      })()}

      {/* ── Step: Chest (men only) ── */}
      {step === "chest" && !showGenderSpecificDetails && (
        isMobile ? (
          <BodyShapeStepMobile
            key="step-chest-mobile"
            stepNumber={steps.indexOf("chest") + 1}
            totalSteps={steps.length}
            title={t("WHICH BEST DESCRIBES YOUR CHEST?")}
            options={[
              { value: "narrow", label: t("Narrow"), img: BODY_IMAGES.male.chest.narrow },
              { value: "average", label: t("Average"), img: BODY_IMAGES.male.chest.average },
              { value: "broad", label: t("Broad"), img: BODY_IMAGES.male.chest.broad },
            ]}
            selected={chestProfile}
            onSelect={(v) => setChestProfile(v as ChestProfile)}
            t={t}
          />
        ) : (
        <div className="ps-bp-step ps-bp-step-enter ps-bp-single-q" key="step-chest">
          <h2 className="ps-bp-title">{t("Which best describes your chest?")}</h2>
          <div className="ps-bp-shape-row ps-bp-shape-row-full">
            <ImageOptionCard img={BODY_IMAGES.male.chest.narrow} label={t("Narrow")} selected={chestProfile === "narrow"} onSelect={() => setChestProfile("narrow")} onHover={() => setHoverDesc(t("A slimmer, narrower chest build"))} onLeave={() => setHoverDesc("")} />
            <ImageOptionCard img={BODY_IMAGES.male.chest.average} label={t("Average")} selected={chestProfile === "average"} onSelect={() => setChestProfile("average")} onHover={() => setHoverDesc(t("A moderate, proportionate chest"))} onLeave={() => setHoverDesc("")} />
            <ImageOptionCard img={BODY_IMAGES.male.chest.broad} label={t("Broad")} selected={chestProfile === "broad"} onSelect={() => setChestProfile("broad")} onHover={() => setHoverDesc(t("A wider, more muscular chest"))} onLeave={() => setHoverDesc("")} />
          </div>
          <p className="ps-bp-hover-hint">{hoverDesc || "\u00A0"}</p>
        </div>
        )
      )}

      {/* ── Step: Midsection / Stomach ── */}
      {step === "midsection" && (
        isMobile ? (
          <BodyShapeStepMobile
            key="step-midsection-mobile"
            stepNumber={steps.indexOf("midsection") + 1}
            totalSteps={steps.length}
            title={t("WHICH BEST DESCRIBES YOUR MIDSECTION?")}
            options={(showGenderSpecificDetails
              ? [
                  { value: "flat", label: t("Flat"), img: BODY_IMAGES.female.stomach.flat },
                  { value: "average", label: t("Average"), img: BODY_IMAGES.female.stomach.average },
                  { value: "round", label: t("Full"), img: BODY_IMAGES.female.stomach.round },
                ]
              : [
                  { value: "flat", label: t("Flat"), img: BODY_IMAGES.male.stomach.flat },
                  { value: "average", label: t("Average"), img: BODY_IMAGES.male.stomach.average },
                  { value: "round", label: t("Full"), img: BODY_IMAGES.male.stomach.round },
                ]) as Array<{ value: "flat" | "average" | "round"; label: string; img: string }>}
            selected={midsectionProfile}
            onSelect={(v) => setMidsectionProfile(v as MidsectionProfile)}
            t={t}
          />
        ) : (
        <div className="ps-bp-step ps-bp-step-enter ps-bp-single-q" key="step-midsection">
          <h2 className="ps-bp-title">{t("Which best describes your midsection?")}</h2>
          <div className="ps-bp-shape-row ps-bp-shape-row-full">
            {showGenderSpecificDetails ? (
              <>
                <ImageOptionCard img={BODY_IMAGES.female.stomach.flat} label={t("Flat")} selected={midsectionProfile === "flat"} onSelect={() => setMidsectionProfile("flat")} onHover={() => setHoverDesc(t("A flatter midsection with minimal protrusion"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.female.stomach.average} label={t("Average")} selected={midsectionProfile === "average"} onSelect={() => setMidsectionProfile("average")} onHover={() => setHoverDesc(t("A moderate, natural midsection shape"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.female.stomach.round} label={t("Full")} selected={midsectionProfile === "round"} onSelect={() => setMidsectionProfile("round")} onHover={() => setHoverDesc(t("A fuller, more rounded midsection"))} onLeave={() => setHoverDesc("")} />
              </>
            ) : (
              <>
                <ImageOptionCard img={BODY_IMAGES.male.stomach.flat} label={t("Flat")} selected={midsectionProfile === "flat"} onSelect={() => setMidsectionProfile("flat")} onHover={() => setHoverDesc(t("A flatter midsection with minimal protrusion"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.male.stomach.average} label={t("Average")} selected={midsectionProfile === "average"} onSelect={() => setMidsectionProfile("average")} onHover={() => setHoverDesc(t("A moderate, natural midsection shape"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.male.stomach.round} label={t("Full")} selected={midsectionProfile === "round"} onSelect={() => setMidsectionProfile("round")} onHover={() => setHoverDesc(t("A fuller, more rounded midsection"))} onLeave={() => setHoverDesc("")} />
              </>
            )}
          </div>
          <p className="ps-bp-hover-hint">{hoverDesc || "\u00A0"}</p>
        </div>
        )
      )}

      {/* ── Step: Seat / Butt ── */}
      {step === "seat" && (
        isMobile ? (
          <BodyShapeStepMobile
            key="step-seat-mobile"
            stepNumber={steps.indexOf("seat") + 1}
            totalSteps={steps.length}
            title={t("WHICH BEST DESCRIBES YOUR SEAT?")}
            options={(showGenderSpecificDetails
              ? [
                  { value: "narrow", label: t("Flat"), img: BODY_IMAGES.female.seat.narrow },
                  { value: "average", label: t("Average"), img: BODY_IMAGES.female.seat.average },
                  { value: "full", label: t("Full"), img: BODY_IMAGES.female.seat.full },
                ]
              : [
                  { value: "narrow", label: t("Flat"), img: BODY_IMAGES.male.seat.narrow },
                  { value: "average", label: t("Average"), img: BODY_IMAGES.male.seat.average },
                  { value: "full", label: t("Full"), img: BODY_IMAGES.male.seat.full },
                ]) as Array<{ value: "narrow" | "average" | "full"; label: string; img: string }>}
            selected={seatProfile}
            onSelect={(v) => setSeatProfile(v as HipProfile)}
            t={t}
          />
        ) : (
        <div className="ps-bp-step ps-bp-step-enter ps-bp-single-q" key="step-seat">
          <h2 className="ps-bp-title">{t("Which best describes your seat?")}</h2>
          <div className="ps-bp-shape-row ps-bp-shape-row-full">
            {showGenderSpecificDetails ? (
              <>
                <ImageOptionCard img={BODY_IMAGES.female.seat.narrow} label={t("Flat")} selected={seatProfile === "narrow"} onSelect={() => setSeatProfile("narrow")} onHover={() => setHoverDesc(t("A flatter backside with less projection"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.female.seat.average} label={t("Average")} selected={seatProfile === "average"} onSelect={() => setSeatProfile("average")} onHover={() => setHoverDesc(t("A moderate, natural backside shape"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.female.seat.full} label={t("Full")} selected={seatProfile === "full"} onSelect={() => setSeatProfile("full")} onHover={() => setHoverDesc(t("A fuller, more rounded backside"))} onLeave={() => setHoverDesc("")} />
              </>
            ) : (
              <>
                <ImageOptionCard img={BODY_IMAGES.male.seat.narrow} label={t("Flat")} selected={seatProfile === "narrow"} onSelect={() => setSeatProfile("narrow")} onHover={() => setHoverDesc(t("A flatter backside with less projection"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.male.seat.average} label={t("Average")} selected={seatProfile === "average"} onSelect={() => setSeatProfile("average")} onHover={() => setHoverDesc(t("A moderate, natural backside shape"))} onLeave={() => setHoverDesc("")} />
                <ImageOptionCard img={BODY_IMAGES.male.seat.full} label={t("Full")} selected={seatProfile === "full"} onSelect={() => setSeatProfile("full")} onHover={() => setHoverDesc(t("A fuller, more rounded backside"))} onLeave={() => setHoverDesc("")} />
              </>
            )}
          </div>
          <p className="ps-bp-hover-hint">{hoverDesc || "\u00A0"}</p>
        </div>
        )
      )}

      {/* ── Step: Hips (women only) ── */}
      {step === "hips" && showGenderSpecificDetails && (
        isMobile ? (
          <BodyShapeStepMobile
            key="step-hips-mobile"
            stepNumber={steps.indexOf("hips") + 1}
            totalSteps={steps.length}
            title={t("WHICH BEST DESCRIBES YOUR HIPS?")}
            options={[
              { value: "narrow", label: t("Narrow"), img: BODY_IMAGES.female.hips.narrow },
              { value: "average", label: t("Average"), img: BODY_IMAGES.female.hips.average },
              { value: "full", label: t("Curvy"), img: BODY_IMAGES.female.hips.full },
            ]}
            selected={hipProfile}
            onSelect={(v) => setHipProfile(v as HipProfile)}
            t={t}
          />
        ) : (
        <div className="ps-bp-step ps-bp-step-enter ps-bp-single-q" key="step-hips">
          <h2 className="ps-bp-title">{t("Which best describes your hips?")}</h2>
          <div className="ps-bp-shape-row ps-bp-shape-row-full">
            <ImageOptionCard img={BODY_IMAGES.female.hips.narrow} label={t("Narrow")} selected={hipProfile === "narrow"} onSelect={() => setHipProfile("narrow")} onHover={() => setHoverDesc(t("Hips closer to shoulder width"))} onLeave={() => setHoverDesc("")} />
          <ImageOptionCard img={BODY_IMAGES.female.hips.average} label={t("Average")} selected={hipProfile === "average"} onSelect={() => setHipProfile("average")} onHover={() => setHoverDesc(t("Hips proportionate to shoulders"))} onLeave={() => setHoverDesc("")} />
            <ImageOptionCard img={BODY_IMAGES.female.hips.full} label={t("Curvy")} selected={hipProfile === "full"} onSelect={() => setHipProfile("full")} onHover={() => setHoverDesc(t("Hips wider than shoulders"))} onLeave={() => setHoverDesc("")} />
          </div>
          <p className="ps-bp-hover-hint">{hoverDesc || "\u00A0"}</p>
        </div>
        )
      )}

      {/* ── Step: Bra Size (women only — last step) ── */}
      {step === "bra" && showGenderSpecificDetails && (
        <div className="ps-bp-step ps-bp-step-enter ps-bp-bra-step" key="step-bra">
          <h2 className="ps-bp-title">{t("Select your bra size")}</h2>

          <div className="ps-bp-bra-section">
            <div className="ps-bp-bra-header">
              <span className="ps-bp-bra-label">{t("BAND SIZE")}</span>
              <div className="ps-bp-bra-region-wrap">
                <button
                  className="ps-bp-bra-region-trigger"
                  onClick={() => setBraRegionOpen(!braRegionOpen)}
                  type="button"
                >
                  {braSizeRegion} <span className={`ps-bp-bra-region-arrow${braRegionOpen ? " ps-open" : ""}`}>&#9662;</span>
                </button>
                {braRegionOpen && (
                  <div className="ps-bp-bra-region-dropdown">
                    {["US","UK","EU","FR","IT","JP"].map(r => (
                      <button
                        key={r}
                        className={`ps-bp-bra-region-item${braSizeRegion === r ? " ps-selected" : ""}`}
                        onClick={() => { setBraSizeRegion(r); setBandSize(null); setCupSize(null); setBraRegionOpen(false); }}
                        type="button"
                      >{r}</button>
                    ))}
                  </div>
                )}
              </div>
              {bandSize && cupSize && (() => {
                const bust = getBustMeasurement(bandSize, cupSize, braSizeRegion);
                return (
                  <span className="ps-bp-bra-inline-result">
                    <strong>{bandSize}{cupSize}</strong>
                    {bust && <span> · {bust.cm}cm</span>}
                  </span>
                );
              })()}
            </div>
            <div className="ps-bp-bra-grid">
              {bandOptions.map(s => (
                <button
                  key={s}
                  className={`ps-bp-bra-btn${bandSize === s ? " ps-bp-bra-btn-selected" : ""}`}
                  onClick={() => setBandSize(s)}
                  type="button"
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="ps-bp-bra-section">
            <span className="ps-bp-bra-label">{t("CUP SIZE")}</span>
            <div className="ps-bp-bra-grid">
              {(CUP_SIZES[braSizeRegion] || CUP_SIZES.US).map(s => (
                <button
                  key={s}
                  className={`ps-bp-bra-btn${cupSize === s ? " ps-bp-bra-btn-selected" : ""}`}
                  onClick={() => setCupSize(s)}
                  type="button"
                >{s}</button>
              ))}
            </div>
          </div>

          {error && <p className="ps-bp-error">{error}</p>}
        </div>
      )}

      {/* ── Navigation ── */}
      {/* Hidden on mobile basics: BasicsStepMobile renders its own
          Next button and bottom tabs in a single cohesive action bar. */}
      {!(isMobile && step === "basics") && (() => {
        // If the user has an active profile with stored measurements AND
        // they're on the basics step, the Next button becomes "See Your
        // Fit" and triggers the fast-path callback (skips the rest
        // of the wizard, runs /sizing/recommend with stored measurements).
        const useProfileFast = step === "basics" && hasActiveProfileWithMeasurements && !!onUseActiveProfile;
        const handleClick = useProfileFast
          ? (shouldOfferStylePreview ? openStylePreview : () => onUseActiveProfile?.(undefined, hUnit === "in" ? "in" : "cm"))
          : handleNext;
        // Append the selected unit system (Metric / Imperial) to the CTA so
        // the user knows how their typed values will be interpreted before
        // they submit. Only annotate the sizing-triggering CTAs, not "Next".
        const unitSuffix = getUnitLabel(hUnit) ? ` (${getUnitLabel(hUnit)})` : "";
        const profileExperienceLoading = useProfileFast && stylePreviewAvailability === "loading";
        const label = useProfileFast
          ? profileExperienceLoading
            ? t("Loading Outfit Builder…")
            : shouldOfferStylePreview
              ? t("Build My Outfit")
              : t("See Your Fit") + unitSuffix
          : (isLastStep ? t("See Your Fit") + unitSuffix : t("Next"));
        return (
          <div className="ps-bp-nav">
            {step !== "basics" ? (
              <button className="ps-bp-back-btn" onClick={handleBackStep} type="button">
                <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
              </button>
            ) : <span />}
            <button
              className={`ps-bp-next-btn${(!useProfileFast && !canProceed) ? " ps-bp-btn-disabled" : ""}`}
              onClick={handleClick}
              disabled={profileExperienceLoading || (!useProfileFast && !canProceed)}
              type="button"
            >
              {label}
            </button>
          </div>
        );
      })()}
      </div>
    </div>
    </div>
  );
}
